"use client";

import { useCallback } from "react";
import { useLocale } from "@/contexts/LocaleContext";
import { ES } from "./i18n.es";
import { TR } from "./i18n.tr";
import { RU } from "./i18n.ru";

/**
 * Lightweight EN→KA/ES/PT-BR/TR copy layer for the mini-game prototypes. Keys are the
 * English strings themselves (with {slot} placeholders); anything missing
 * from the dictionary falls back to the English key, so untranslated copy
 * renders unchanged rather than breaking.
 */
export type MiniLocale = "en" | "ka" | "es" | "tr" | "ru" | "pt" | "de";

const KA: Record<string, string> = {
  // Guess the Goal (live)
  "Guess the Goal": "გამოიცანი გოლი",
  "Name the iconic goal — real rewards, server-scored": "ამოიცანი ლეგენდარული გოლი — რეალური ჯილდოები",
  "Solved {n}/{total}": "ამოცნობილი {n}/{total}",
  "+{points} points": "+{points} ქულა",
  "Move {n}/{total}": "სვლა {n}/{total}",
  "Which goal is this?": "რომელი გოლია ეს?",
  "Bonus question": "ბონუს კითხვა",
  "Next goal": "შემდეგი გოლი",
  "Guess the Goal is warming up — check back soon": "გამოიცანი გოლი მალე ჩაირთვება — შემოიარე ცოტა ხანში",
  "A legendary goal replays on the coaching board. The earlier you name it, the more you earn — first solve of each goal pays coins and XP.":
    "ლეგენდარული გოლი ტაქტიკურ დაფაზე თამაშდება. რაც უფრო ადრე ამოიცნობ, მეტს გამოიმუშავებ — ყოველი გოლის პირველი ამოცნობა ქოინებსა და XP-ს გაძლევს.",
  "(daily cap)": "(დღიური ლიმიტი)",
  "Name the legendary goal from the coaching board": "გამოიცანი ლეგენდარული გოლი სამწვრთნელო დაფიდან",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal. Sign in to earn coins and XP.": "ლეგენდარული გოლი სამწვრთნელო დაფაზე მეორდება. უყურე სვლებს და გამოიცანი გოლი. შედი ანგარიშზე, რომ ქოინები და XP დააგროვო.",
  "Sign in to earn coins and XP for every goal you solve": "შედი ანგარიშზე და ყოველ გამოცნობილ გოლზე ქოინები და XP მიიღე",
  "Already solved before — no repeat rewards": "უკვე ამოცნობილი გაქვს — განმეორებაზე ჯილდო აღარ გეძლევა",
  "Something went wrong — try again": "რაღაც შეცდომაა — სცადე თავიდან",
  "Watch the real goal": "ნახე ნამდვილი გოლი",
  "Back to the board": "დაფაზე დაბრუნება",
  "Board": "დაფა",
  "Video": "ვიდეო",
  "Replay board": "გაიმეორე სვლები",
  "Couldn't load your game": "თამაში ვერ ჩაიტვირთა",

  // Shared
  "Prototype — virtual points only, no real money or rewards":
    "პროტოტიპი — მხოლოდ ვირტუალური ქულები, რეალური ფული ან ჯილდო არ არსებობს",
  "Points": "ქულები",
  "Play again": "თავიდან თამაში",
  "Try again": "სცადე თავიდან",
  "Next": "შემდეგი",
  "Back": "უკან",
  "Opponent": "მეტოქე",

  // Accumulator
  "Accumulator": "ექსპრესი",
  "Five legs. One stake. All must land.": "ხუთი პოზიცია. ერთი ფსონი. ყველა უნდა შევიდეს.",
  "Pick 5 legs · {n}/5": "აირჩიე 5 პოზიცია · {n}/5",
  "Continue · {odds}x": "გაგრძელება · {odds}x",
  "Potential return": "შესაძლო მოგება",
  "Your stake": "შენი ფსონი",
  "Place bet · {stake}": "დადე ფსონი · {stake}",
  "Back to legs": "პოზიციებზე დაბრუნება",
  "Return so far": "ამ დროისთვის მოგება",
  "4 of 5 landed!": "5-დან 4 შევიდა!",
  "Take the money or go for it?": "აიღებ ფულს თუ გარისკავ?",
  "One leg left. Cash out now, or risk it for the full {amount}.":
    "დარჩა ერთი პოზიცია. დააქეშაუთე ახლა, ან გარისკე სრული {amount}-სთვის.",
  "Cash out": "ქეშაუთი",
  "Final leg": "ბოლო პოზიცია",
  "Bet won!": "ფსონი მოგებულია!",
  "Busted on leg {n}": "ჩაიშალა მე-{n} პოზიციაზე",
  "Lost your {stake} stake — one leg let it down.": "დაკარგე {stake} ფსონი — ერთმა პოზიციამ ჩაშალა.",
  "New accumulator": "ახალი ექსპრესი",
  "if it lands:": "თუ შევა:",
  "{leg}/{total} legs": "{leg}/{total} პოზიცია",
  "Leg {n}": "პოზიცია {n}",

  // Bet Slip Booster
  "Answer right to boost your odds": "უპასუხე სწორად და გაზარდე კოეფიციენტი",
  "Bet Slip · Treble": "ტალონი · ტრებლი",
  "3 selections": "3 არჩევანი",
  "boosted": "გაზრდილი",
  "Total odds": "ჯამური კოეფიციენტი",
  "Boost leg {n} · {club}": "გაზარდე პოზიცია {n} · {club}",
  "Boosted {a} → {b}": "გაიზარდა {a} → {b}",
  "Stake {stake} · potential return": "ფსონი {stake} · შესაძლო მოგება",
  "Slip landed!": "ტალონი შევიდა!",
  "Not this time": "ამჯერად ვერა",
  "The boosted {odds}x didn't come in.": "გაზრდილი {odds}x ვერ შევიდა.",
  "New slip": "ახალი ტალონი",

  // Cash Out Ladder
  "Bank it or climb — one wrong answer wipes it all": "აიღე ან აძვერი — ერთი შეცდომა ყველაფერს შლის",
  "Stake": "ფსონი",
  "Wiped": "განულდა",
  "Banked": "აღებულია",
  "Pot at risk": "რისკის ქვეშ",
  "{mult}x on {stake}": "{mult}x {stake}-ზე",
  "Stake {stake} & climb": "დადე {stake} და აძვერი",
  "Bank {pot} now, or risk it for {next}?": "აიღებ {pot}-ს ახლა, თუ გარისკავ {next}-სთვის?",
  "Bank {pot}": "აიღე {pot}",
  "Climb to {mult}x": "აძვერი {mult}x-ზე",
  "Wiped!": "განულდა!",
  "Wrong answer at {mult}x — the whole pot is gone.": "არასწორი პასუხი {mult}x-ზე — მთელი ბანკი დაიკარგა.",
  "Banked {amount}": "აღებულია {amount}",
  "😱 It would have hit 32x!": "😱 32x-მდე ავიდოდა!",
  "Smart! You'd have busted at {mult}x.": "ჭკვიანურად! {mult}x-ზე ჩაიშლებოდი.",
  "It would've climbed to {a}x then busted at {b}x.": "{a}x-მდე ავიდოდა და {b}x-ზე ჩაიშლებოდა.",
  "Go again": "კიდევ ერთხელ",

  // Daily Jackpot
  "One hard question. Winner takes the pot.": "ერთი რთული კითხვა. გამარჯვებული იღებს ბანკს.",
  "Today's pot": "დღევანდელი ბანკი",
  "climbing with every miss…": "ყოველ შეცდომაზე იზრდება…",
  "failed today": "დღეს ვერ გატეხა",
  "next question": "შემდეგი კითხვა",
  "Hard · one attempt": "რთული · ერთი ცდა",
  "Rolled over": "გადავიდა",
  "Not this time — your entry fed the pot. It now sits at {pot}. Come back for the next question in {time}.":
    "ამჯერად ვერა — შენი ცდა ბანკს დაემატა. ახლა {pot}-ია. დაბრუნდი შემდეგ კითხვაზე {time}-ში.",
  "Replay": "თავიდან",
  "Jackpot won!": "ჯექპოტი მოგებულია!",
  "The pot resets for tomorrow's question.": "ბანკი ხვალინდელი კითხვისთვის განულდება.",

  // Half-Time Trivia
  "Live": "ლაივი",
  "Half-Time Quiz": "შესვენების ქვიზი",
  "Question {n} / {total}": "კითხვა {n} / {total}",
  "Get {n}+ for a free bet · {c} right": "უპასუხე {n}+ სწორად უფასო ფსონისთვის · {c} სწორია",
  "Free bet won!": "უფასო ფსონი მოგებულია!",
  "{c}/{total} — so close": "{c}/{total} — ცოტა დააკლდა",
  "You earned a virtual second-half free bet (demo — no real rewards).":
    "მოიგე მეორე ტაიმის ვირტუალური უფასო ფსონი (დემო — რეალური ჯილდო არ არის).",
  "Get {n}+ right before kickoff to earn a free bet.":
    "უპასუხე {n}+ კითხვას სწორად მეორე ტაიმის დაწყებამდე უფასო ფსონისთვის.",
  "Markets": "მარკეტები",
  "Odds are illustrative · prototype": "კოეფიციენტები საილუსტრაციოა · პროტოტიპი",
  "Prototype — virtual only": "პროტოტიპი — მხოლოდ ვირტუალური",

  // Odds Board
  "Odds Board": "კოეფიციენტების დაფა",
  "Every answer is a market — back your call": "ყველა პასუხი მარკეტია — დაუჭირე მხარი შენსას",
  "Market {n}": "მარკეტი {n}",
  "{pct}% implied": "{pct}% ალბათობა",
  "returns": "მოგება",
  "Pick an answer to back": "აირჩიე პასუხი ფსონისთვის",
  "Place {stake} on {answer}": "დადე {stake} — {answer}",
  "Won {amount}!": "მოგებულია {amount}!",
  "Lost {amount}": "წაგებულია {amount}",
  "Big-odds call!": "დიდი კოეფიციენტის სვლა!",
  "Right answer:": "სწორი პასუხი:",
  "Next market": "შემდეგი მარკეტი",

  // Pass Chain
  "Pass Chain": "პასების ჯაჭვი",
  "Link the two players through shared clubs": "დააკავშირე ორი ფეხბურთელი საერთო კლუბებით",
  "Fewer links score higher": "ნაკლები რგოლი მეტ ქულას იძლევა",
  "Par —": "პარი —",
  "Par {par} links": "პარი {par} რგოლი",
  "Who links to {name}?": "ვინ უკავშირდება {name}-ს?",
  "Add": "დამატება",
  "Already in the chain": "უკვე ჯაჭვშია",
  "Unknown player — try another": "უცნობი ფეხბურთელი — სცადე სხვა",
  "{a} never played with {b}": "{a}-ს არ უთამაშია {b}-სთან",
  "Reset": "განულება",
  "Linked!": "დაკავშირდა!",
  "{n} links": "{n} რგოლი",
  "Par — perfect!": "პარი — იდეალური!",
  "par {n}": "პარი {n}",
  "Next chain": "შემდეგი ჯაჭვი",
  "START": "სტარტი",
  "TARGET": "მიზანი",

  // Penalty Shootout
  "Penalty Shootout": "პენალტების სერია",
  "Round {n} / {total}": "რაუნდი {n} / {total}",
  "You · AI": "შენ · AI",
  "Answer to earn your shot": "უპასუხე და მოიგე დარტყმა",
  "Opponent is stepping up…": "მოწინააღმდეგე ემზადება…",
  "AI is on target — get ready to dive!": "AI ზუსტია — მოემზადე დასაჭერად!",
  "AI answering…": "AI პასუხობს…",
  "Pick your corner — beat the keeper": "აირჩიე კუთხე — აჯობე მეკარეს",
  "You're in goal — pick your dive!": "შენ კარში ხარ — აირჩიე მხარე!",
  "GOAL!": "გოლი!",
  "MISSED": "აცდენა",
  "AI SCORES": "AI-მ გაიტანა",
  "SAVED!": "დაჭერილია!",
  "Opponent's turn": "მოწინააღმდეგის ჯერი",
  "See result": "შედეგის ნახვა",
  "Next round": "შემდეგი რაუნდი",
  "You win!": "მოიგე!",
  "Draw": "ფრე",
  "You lose": "წააგე",

  // Free Kicks (formerly Final Third)
  "Free Kicks": "Free Kicks",
  "Know football. Read the goal. Take the shot.": "იცოდე ფეხბურთი. აირჩიე კუთხე. დაარტყი.",
  "Balance": "ბალანსი",
  "Pot": "ბანკი",
  "Your pot": "შენი ბანკი",
  "Stake {n} & attack": "დადე {n} და შეუტიე",
  "Min {n}": "მინ. {n}",
  "Top up (demo)": "შევსება (დემო)",
  "Answer to scout the keeper": "უპასუხე და დაზვერე მეკარე",
  "{n}s": "{n} წმ",
  "Time's up — blind shot": "დრო ამოიწურა — ბრმა დარტყმა",
  "Wrong — the correct answer stays. Blind shot.": "არასწორია — სწორი პასუხი რჩება. ბრმა დარტყმა.",
  "Answer to scout the keeper — knowledge sharpens the shot, the risk stays.":
    "უპასუხე და დაზვერე მეკარე — ცოდნა დარტყმას ალესავს, რისკი კი რჩება.",
  "Attack {n}": "შეტევა {n}",
  "SCOUT REPORT ✓ — one save zone revealed": "დაზვერვის ანგარიში ✓ — ერთი სეივ-ზონა გამოვლინდა",
  "No scout — blind shot pays more": "დაზვერვის გარეშე — ბრმა დარტყმა მეტს იხდის",
  "Pick your shot zone": "აირჩიე დარტყმის ზონა",
  "4 GOAL · 2 SAVE": "4 გოლი · 2 სეივი",
  "4 GOAL · 1 SAVE": "4 გოლი · 1 სეივი",
  "TAKE {amount}": "აიღე {amount}",
  "NEXT ATTACK": "შემდეგი შეტევა",
  "Next attack risks the whole pot — the keeper resets.": "შემდეგი შეტევა მთელ ბანკს რისკავს — მეკარე თავიდან დგება.",
  "The keeper read it — pot lost.": "მეკარემ წაიკითხა — ბანკი დაიკარგა.",
  "Cashed out {amount}!": "განაღდებულია {amount}!",
  "Loading…": "იტვირთება…",
  "Could not load balance — tap to retry": "ბალანსი ვერ ჩაიტვირთა — დააჭირე თავიდან საცდელად",
  "New round": "ახალი რაუნდი",
  "Live wins": "ლაივ მოგებები",
  "Longest runs today": "დღის ყველაზე გრძელი სერიები",
  "{n} playing now": "{n} თამაშობს ახლა",
  "{pct}% answered correctly": "{pct}%-მა სწორად უპასუხა",
  "{pct}% scored": "{pct}%-მა გაიტანა",
  "{pct}% cashed out": "{pct}%-მა აიღო",
  "{n} going NEXT ATTACK": "{n} აგრძელებს შეტევას",
  "You": "შენ",
  "just now": "ახლახან",
  "{n}m ago": "{n} წთ წინ",

  // Squad Collection
  "Squad Collection": "გუნდის კოლექცია",
  "Answer to pull cards. Build your XI.": "უპასუხე, ამოიღე ბარათები და ააწყვე შენი 11-ეული.",
  "Squad": "შემადგენლობა",
  "Answer to earn a pack": "უპასუხე და მოიგე პაკეტი",
  "Tap to open": "დააჭირე გასახსნელად",
  "Add to squad": "შემადგენლობაში დამატება",
  "Squad complete!": "შემადგენლობა სრულია!",
  "Reward: +2,000 🪙": "ჯილდო: +2,000 🪙",
  "Start a new squad": "ახალი შემადგენლობა",
  "Bronze": "ბრინჯაო",
  "Silver": "ვერცხლი",
  "Gold": "ოქრო",
  "Special": "სპეციალური",

  // Squad Spin
  "Easy": "მარტივი",
  "Hard": "რთული",
  "Expert": "ექსპერტი",
  "Name a player who fits every reel": "დაასახელე ფეხბურთელი, რომელიც ყველა უჯრას ერგება",
  "{n} reels": "{n} უჯრა",
  "Spin": "დატრიალება",
  "Spinning…": "ტრიალებს…",
  "Name a player…": "დაასახელე ფეხბურთელი…",
  "Go": "გო",
  "Hold a reel, respin the rest": "დააფიქსირე უჯრა და დაატრიალე დანარჩენი",
  "Respin −{n}": "თავიდან დატრიალება −{n}",
  "Last 3 spins": "ბოლო 3 დატრიალება",
  "Missed": "აცდენა",
  "Correct!": "სწორია!",
  "Only {pct}% named this": "ეს მხოლოდ {pct}%-მა დაასახელა",
  "rarity": "იშვიათობა",
  "Time's up": "დრო ამოიწურა",
  "You could've said:": "შეგეძლო გეთქვა:",
  "No valid player for that combo!": "ამ კომბინაციაზე ფეხბურთელი არ არსებობს!",
  "Spin again": "კიდევ დაატრიალე",
  "Hold reel": "უჯრის დაფიქსირება",
  "Release reel": "უჯრის გათავისუფლება",
  "Club": "კლუბი",
  "Position": "პოზიცია",
  "Nation": "ქვეყანა",
  "Era": "ეპოქა",
  "Trophy": "თასი",

  // Trivia Spin
  "easy": "მარტივი",
  "medium": "საშუალო",
  "hard": "რთული",
  "Answer to earn spins — the wheel pays out": "უპასუხე, მოაგროვე დატრიალებები — ბორბალი გადაიხდის",
  "Coins": "ქოინები",
  "Question {n}": "კითხვა {n}",
  "+1 spin earned": "+1 დატრიალება მოგებულია",
  "No spin — try the next one": "დატრიალება ვერ მოიგე — სცადე შემდეგი",
  "{n} spins available": "{n} დატრიალება გაქვს",
  "JACKPOT!": "ჯექპოტი!",
  "Spin the wheel": "დაატრიალე ბორბალი",
  "Answer to earn a spin": "უპასუხე დატრიალების მოსაგებად",

  // Data labels (fixtures / bet slip picks) — rendered through t() at the
  // component level, keyed by their English data values.
  "Full Time Result": "მატჩის შედეგი",
  "Next Goal": "შემდეგი გოლი",
  "Total Goals Over/Under 3.5": "გოლების ჯამი 3.5 მეტი/ნაკლები",
  "Both Teams to Score — 2nd Half": "ორივე გაიტანს — მეორე ტაიმი",
  "No more goals": "გოლი აღარ იქნება",
  "Over 3.5": "3.5-ზე მეტი",
  "Under 3.5": "3.5-ზე ნაკლები",
  "Yes": "კი",
  "No": "არა",
  "Champions League · Group Stage": "ჩემპიონთა ლიგა · ჯგუფური ეტაპი",
  "Real Madrid to win": "რეალის მოგება",
  "Man City to win": "მან სიტის მოგება",
  "Bayern to win": "ბაიერნის მოგება",
  "Goalkeeper": "მეკარე",
  "Zone": "ზონა",
  "Defender": "მცველი",
  "Midfielder": "ნახევარმცველი",
  "Forward": "თავდამსხმელი",
  "World Cup": "მსოფლიო თასი",
  "League": "ლიგა",
  "Champions League": "ჩემპიონთა ლიგა",
  "League Title": "ჩემპიონობა",

  // Football Tic Tac Toe
  "Football Tic Tac Toe": "საფეხბურთო იქს-ნული",
  "Claim cells with players — three in a row wins": "დაიკავე უჯრები ფეხბურთელებით — სამი ზედიზედ იგებს",
  "You · Opponent": "შენ · მეტოქე",
  "Pick a cell and name a player who played for that club AND that nation. Your opponent answers back — line up three to win.":
    "აირჩიე უჯრა და დაასახელე ფეხბურთელი, რომელიც ამ კლუბშიც ითამაშა და ამ ქვეყნის ნაკრებშიც. მეტოქე გიპასუხებს — ააწყვე სამი ზედიზედ.",
  "Start match": "მატჩის დაწყება",
  "Your turn — pick a cell": "შენი ჯერია — აირჩიე უჯრა",
  "Not a match — turn passes": "არ ემთხვევა — ჯერი გადადის",
  "Time's up — turn passes": "დრო ამოიწურა — ჯერი გადადის",
  "Pass": "გადაცემა",
  "Opponent blanked — your turn!": "მეტოქემ ვერ გაიხსენა — შენი ჯერია!",
  "Opponent is thinking…": "მეტოქე ფიქრობს…",
  "Opponent wins": "მეტოქე იგებს",
  "Three in a row!": "სამი ზედიზედ!",
  "Board full — {a} cells vs {b}": "დაფა შეივსო — {a} უჯრა {b}-ის წინააღმდეგ",
  "New grid": "ახალი ბადე",
  "{pct}%": "{pct}%",
  "Brazil": "ბრაზილია",
  "France": "საფრანგეთი",
  "Argentina": "არგენტინა",
  "Spain": "ესპანეთი",
  "Netherlands": "ნიდერლანდები",
  "Portugal": "პორტუგალია",

  // Survivor
  "Survivor": "სურვაივერი",
  "One wrong answer and you are out — how long can you last?": "ერთი შეცდომა და გარეთ ხარ — რამდენ ხანს გაძლებ?",
  "Streak": "სერია",
  "Sudden death": "უეცარი სიკვდილი",
  "Questions get harder as you go. {n} virtual players start with you — outlast them all.":
    "კითხვები თანდათან მძიმდება. {n} ვირტუალური მოთამაშე იწყებს შენთან ერთად — გადაასწარი ყველას.",
  "Best streak: {n}": "საუკეთესო სერია: {n}",
  "Enter the arena": "არენაზე შესვლა",
  "Still standing": "ჯერ კიდევ დგანან",
  "Eliminated": "გამოეთიშე",
  "Streak of {n}": "{n}-იანი სერია",
  "You outlasted {pct}% of the field — {left} were still standing.": "გადაასწარი მოთამაშეების {pct}%-ს — {left} ჯერ კიდევ იდგა.",
  "New best!": "ახალი რეკორდი!",
  "Run it back": "კიდევ ერთხელ",
  "Last one standing!": "უკანასკნელი გადარჩენილი!",
  "You cleared every question in the bank — a perfect {n}-answer run.": "ბანკის ყველა კითხვას უპასუხე — იდეალური {n}-პასუხიანი სერია.",

  // Hi-Lo Ride
  "Hi-Lo Ride": "Hi-Lo Ride",
  "Call the higher stat — odds priced by how many get it right": "გამოიცანი მეტი სტატისტიკა — კოეფიციენტი სირთულეზეა აწყობილი",
  "Chain higher-or-lower calls. Every correct call multiplies your stake — hard matchups pay more. Cash out before you miss.":
    "ააწყვე მეტი-ნაკლების ჯაჭვი. ყოველი სწორი პასუხი ამრავლებს ფსონს — რთული წყვილები მეტს იხდიან. დააქეშაუთე შეცდომამდე.",
  "Stake {stake} & ride": "დადე {stake} და იმგზავრე",
  "Step {n} · odds": "ნაბიჯი {n} · კოეფ.",
  "{pct}% of players call this one right": "მოთამაშეების {pct}% ამას სწორად პასუხობს",
  "Which is higher?": "რომელია მეტი?",
  "Higher": "მეტი",
  "Pot is {pot}. Next matchup pays {odds}x → {next}.": "ბანკი {pot}-ია. შემდეგი წყვილი იხდის {odds}x → {next}.",
  "Cash out {pot}": "ქეშაუთი {pot}",
  "Ride on": "გააგრძელე",
  "Wrong call": "არასწორი პასუხი",
  "The ride ends — your {stake} stake is gone.": "მგზავრობა დასრულდა — შენი {stake} ფსონი დაიკარგა.",
  "New ride": "ახალი მგზავრობა",
  "Cashed {amount}!": "განაღდებულია {amount}!",
  "{n} correct calls — {mult}x your stake.": "{n} სწორი პასუხი — შენი ფსონის {mult}x.",

  // Trivia Mines
  "Trivia Mines": "Trivia Mines",
  "Dribble past hidden defenders — scout them with your knowledge": "გაუარე დამალულ მცველებს — დაზვერე ისინი ცოდნით",
  "{d} defenders hide in {n} tiles. Every clean dribble grows the pot — hit a defender and lose it all. Answer questions to scout defenders out.":
    "{d} მცველი იმალება {n} უჯრაში. ყოველი სუფთა დრიბლინგი ზრდის ბანკს — მცველზე მოხვედრა ყველაფერს კარგავს. უპასუხე კითხვებს მცველების დასაზვერად.",
  "Stake {stake} & dribble": "დადე {stake} და დაიწყე დრიბლინგი",
  "Tackled": "წაგართვეს",
  "Now · next tile": "ახლა · შემდეგი უჯრა",
  "Scout ({n} left)": "დაზვერვა ({n} დარჩა)",
  "{n} safe tiles opened · {d} defenders in play": "{n} უსაფრთხო უჯრა გახსნილია · {d} მცველი თამაშშია",
  "Scout report — a defender is marked!": "დაზვერვა — ერთი მცველი მონიშნულია!",
  "Scout failed — no intel": "დაზვერვა ჩაიშალა — ინფორმაცია არაა",
  "Answer to scout a defender": "უპასუხე მცველის დასაზვერად",
  "Tackled!": "წაგართვეს!",
  "A defender got you after {n} clean tiles — the pot is gone.": "მცველმა დაგაკავა {n} სუფთა უჯრის შემდეგ — ბანკი დაიკარგა.",
  "New run": "ახალი გარბენი",
  "{n} clean dribbles at {mult}x.": "{n} სუფთა დრიბლინგი {mult}x-ზე.",

  // Quiz Board
  "Quiz Board": "ქვიზის დაფა",
  "Pick tiles, bank the value — steal when the AI slips": "აირჩიე უჯრები, ჩაიბარე ქულები — მოიპარე, როცა AI შეცდება",
  "Nine tiles, three value tiers. Answer to bank the tile and keep control — miss and the AI can steal. Highest bank wins.":
    "ცხრა უჯრა, სამი ღირებულება. უპასუხე, ჩაიბარე უჯრა და შეინარჩუნე კონტროლი — შეცდომაზე AI იპარავს. მეტი ბანკი იგებს.",
  "Start game": "თამაშის დაწყება",
  "Clubs": "კლუბები",
  "Legends": "ლეგენდები",
  "Tournaments": "ტურნირები",
  "Your board — pick a tile": "შენი დაფაა — აირჩიე უჯრა",
  "AI is picking a tile…": "AI ირჩევს უჯრას…",
  "AI plays for {v}": "AI თამაშობს {v}-ზე",
  "AI banks it": "AI იბარებს",
  "AI is wrong — steal it!": "AI ცდება — მოიპარე!",
  "STEAL for {v}!": "მოპარვა {v}-ზე!",
  "For {v}": "{v}-ზე",
  "Final banks — you {a}, AI {b}.": "საბოლოო ბანკები — შენ {a}, AI {b}.",
  "New board": "ახალი დაფა",

  // Last One Standing
  "Last One Standing": "უკანასკნელი გადარჩენილი",
  "{n} enter, one survives — make every cut": "{n} იწყებს, ერთი რჩება — გადაურჩი ყველა გადარჩევას",
  "Alive": "ცოცხალია",
  "Battle royale quiz": "ბეთლ-როიალ ქვიზი",
  "Every round the field is cut — answer right and fast to stay in it. Nine rounds from {n} players down to one.":
    "ყოველ რაუნდში ველი მცირდება — უპასუხე სწორად და სწრაფად, რომ დარჩე. ცხრა რაუნდი {n} მოთამაშიდან ერთამდე.",
  "Kick off": "დაწყება",
  "Continue": "გააგრძელე",
  "A legendary goal replays on the coaching board. Name it — the first solve of each goal pays coins and XP.":
    "ლეგენდარული გოლი საწვრთნელ დაფაზე მეორდება. ამოიცანი — ყოველი გოლის პირველი ამოცნობა ქოინებსა და XP-ს იძლევა.",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal.":
    "ლეგენდარული გოლი საწვრთნელ დაფაზე მეორდება. უყურე სვლებს და ამოიცანი გოლი.",
  "Back tomorrow": "ხვალ გნახავთ",
  "All {limit} goals played — come back tomorrow": "დღეს {limit}-ვე გოლი ნათამაშებია — დაბრუნდი ხვალ",
  "{left} of {limit} goals left today": "დღეს დარჩა {left} გოლი {limit}-დან",
  "Daily coins earned — more tomorrow": "დღევანდელი ქოინები აღებულია — მეტი ხვალ",
  "Today: {today}/{cap} coins": "დღეს: {today}/{cap} ქოინი",
  "Your collection": "შენი კოლექცია",
  "Fastest {cap} survive": "ყველაზე სწრაფი {cap} რჩება",
  "Field": "ველი",
  "You made the cut!": "გადარჩი!",
  "#{rank} fastest — {cap} players remain.": "#{rank} სისწრაფით — {cap} მოთამაშე რჩება.",
  "Cut!": "გამოეთიშე!",
  "Finished #{place} of {n}": "დაასრულე #{place} {n}-დან",
  "Round {r} was your last — the field marched on without you.": "რაუნდი {r} შენი ბოლო იყო — ველმა უშენოდ გააგრძელა.",
  "Re-enter": "თავიდან შესვლა",
  "CHAMPION!": "ჩემპიონი!",
  "{n} started. You are the last one standing.": "{n} იწყებდა. შენ ხარ უკანასკნელი გადარჩენილი.",
  "Defend the title": "ტიტულის დაცვა",

  // Golden Goal
  "Golden Goal": "ოქროს გოლი",
  "Sudden death — push the ball into their net": "უეცარი სიკვდილი — შეიტანე ბურთი მათ კარში",
  "Round": "რაუნდი",
  "Both of you answer the same question — faster and righter pushes the ball. Reach ±{n} and it is a goal. First goal wins.":
    "ორივე ერთსა და იმავე კითხვას პასუხობთ — უფრო სწრაფი და ზუსტი წევს ბურთს. მიაღწიე ±{n}-ს და გოლია. პირველი გოლი იგებს.",
  "AI net": "AI-ს კარი",
  "Your attack": "შენი შეტევა",
  "GOLDEN GOAL!": "ოქროს გოლი!",
  "AI scores": "AI-მ გაიტანა",
  "Decided in {n} rounds.": "გადაწყდა {n} რაუნდში.",
  "Rematch": "რევანში",
  "vs": "vs",

  // Career Race
  "Career Race": "კარიერის რბოლა",
  "The transfer trail reveals — buzz before your rival": "კარიერა თანდათან იხსნება — დააჭირე ზარს მეტოქეზე ადრე",
  "Clubs appear one by one. Buzz early for 100 points — every extra club cuts the prize. Wrong buzz and you are out of the round. Best of {n}.":
    "კლუბები სათითაოდ ჩნდება. ადრეული ზარი 100 ქულაა — ყოველი დამატებითი კლუბი პრიზს ამცირებს. არასწორი ზარი რაუნდიდან გაგრიცხავს. საუკეთესო {n}-დან.",
  "Start race": "რბოლის დაწყება",
  "Buzz now: {pts} pts": "ზარი ახლა: {pts} ქულა",
  "Locked out": "დაბლოკილი ხარ",
  "BUZZ!": "ზარი!",
  "Rival buzzed wrong — they are out!": "მეტოქემ არასწორად უპასუხა — გავარდა!",
  "Your rival is watching the same trail…": "შენი მეტოქე იმავე კვალს უყურებს…",
  "Whose career is this?": "ვისი კარიერაა ეს?",
  "Wrong — locked out!": "არასწორია — დაიბლოკე!",
  "⚡ Rival buzzes…": "⚡ მეტოქე აჭერს ზარს…",
  "+{pts} — yours!": "+{pts} — შენია!",
  "Rival takes {pts}": "მეტოქე იღებს {pts}-ს",
  "Nobody got it": "ვერავინ გამოიცნო",
  "It was {name}": "ეს იყო {name}",
  "You win the race!": "რბოლა მოიგე!",
  "Rival wins": "მეტოქე იგებს",
  "Race again": "კიდევ ირბოლე",

  // Stat Sniper
  "Closest Wins": "სტატ სნაიპერი",
  "No options, no help — land your guess on the number": "ვარიანტების გარეშე — მიიტანე ვარაუდი ზუსტ რიცხვამდე",
  "Score": "ქულა",
  "{n} numeric stats — slide to your best guess. The closer you land, the more you score; a perfect hit pays a bullseye bonus.":
    "{n} რიცხვითი სტატისტიკა — გაასრიალე შენს საუკეთესო ვარაუდამდე. რაც უფრო ახლოს დაჯდები, მეტ ქულას იღებ; ზუსტი მოხვედრა ბონუსს იხდის.",
  "Take aim": "დამიზნება",
  "Target {n} / {total}": "სამიზნე {n} / {total}",
  "Lock it in": "დაფიქსირება",
  "Answer": "პასუხი",
  "BULLSEYE! +{pts}": "ათიანში! +{pts}",
  "+{pts} pts": "+{pts} ქულა",
  "You were {diff} off.": "{diff}-ით ააცილე.",
  "Next target": "შემდეგი სამიზნე",
  "Elite sniper!": "ელიტური სნაიპერი!",
  "Sharp shooter": "მკვეთრი მსროლელი",
  "On the range": "პოლიგონზე",
  "Blindfolded": "თვალახვეული",
  "{score} points over {n} targets": "{score} ქულა {n} სამიზნეზე",
  "Reload": "გადატენვა",

  // Free Kicks — boost rules (50/50 shot, answers raise the payout)
  "Every shot is a 50/50 duel with the keeper. Answer questions to boost the payout — a wrong answer resets the boost.":
    "ყოველი დარტყმა მეკარესთან 50/50 დუელია. უპასუხე კითხვებს და გაზარდე კოეფიციენტი — არასწორი პასუხი ბუსტს ანულებს.",
  "Boost {a}/{max} · ×{mult}": "ბუსტი {a}/{max} · ×{mult}",
  "BOOSTED! Payout is now ×{mult}": "ბუსტი! კოეფიციენტი ახლა ×{mult}",
  "Wrong — boost resets to ×{mult}": "არასწორია — ბუსტი ×{mult}-ზე ბრუნდება",
  "50/50 shot · current payout ×{mult}": "50/50 დარტყმა · მიმდინარე კოეფიციენტი ×{mult}",
  "Answer · boost to ×{next}": "უპასუხე · გაზარდე ×{next}-მდე",
  "Boosts raise the payout — the shot stays 50/50.": "ბუსტები კოეფიციენტს ზრდის — დარტყმა 50/50 რჩება.",
  "Boosting…": "იზრდება…",
  "Time's up — boost resets to ×{mult}": "დრო ამოიწურა — ბუსტი ×{mult}-ზე ბრუნდება",
  "Left or right — the keeper picks one": "მარცხნივ თუ მარჯვნივ — მეკარე ერთს ირჩევს",
  "Pick your corner": "აირჩიე კუთხე",
  "Next attack risks the whole pot — the boost resets.": "შემდეგი შეტევა მთელ ბანკს რისკავს — ბუსტი ნულდება.",

  // Free Kicks — run-multiplier HUD
  "Run ×{run} → ×{next}": "სერია ×{run} → ×{next}",
  "Run": "სერია",
  "Answering is locked this attack — take the shot.": "პასუხები ამ შეტევაზე დაკეტილია — დაარტყი.",
  "Sound settings": "ხმის პარამეტრები",
  "Crowd": "გულშემატკივრები",
  "Effects": "ეფექტები",
  "Off": "გამორთ.",
  "More open zones = better odds of keeping the run alive.": "მეტი ღია ზონა = მეტი შანსი, რომ სერია გააგრძელო.",

  // Free Kicks — Option C (open-zones) rules
  "The goal opens with 2 zones and one hidden keeper. Answer questions to open up to 6 — a wrong answer slams it back to 2.":
    "კარი იხსნება 2 ზონით და ერთი დამალული მეკარით. უპასუხე კითხვებს და გახსენი 6-მდე — არასწორი პასუხი ისევ 2-მდე ხურავს.",
  "{k} open · {pct}% · ×{mult}": "{k} ღია · {pct}% · ×{mult}",
  "Zone opened! {k} of {max} in play": "ზონა გაიხსნა! {k} {max}-დან თამაშშია",
  "Pick your angle and shoot · {pot} → {next}": "აირჩიე კუთხე და დაარტყი · {pot} → {next}",
  "Wrong — goal slams back to {n} zones": "არასწორია — კარი ისევ {n} ზონამდე იხურება",
  "{k} zones open · 1 keeper hidden · {pct}% goal": "{k} ზონა ღიაა · 1 მეკარე იმალება · გოლის შანსი {pct}%",
  "Answer · open zone {n}": "უპასუხე · გახსენი ზონა {n}",
  "Shoot · {pot} → {next}": "დაარტყი · {pot} → {next}",
  "More zones = safer shot, smaller payout — the maths favours knowing.":
    "მეტი ზონა = უფრო უსაფრთხო დარტყმა, ნაკლები კოეფიციენტი — მათემატიკა ცოდნის მხარესაა.",
  "Opening the goal…": "კარი იხსნება…",
  "Read the question…": "წაიკითხე კითხვა…",
  "Time's up — the goal slams back to {n} zones": "დრო ამოიწურა — კარი ისევ {n} ზონამდე იხურება",
  "Wrong — the goal slams back to {n} zones": "არასწორია — კარი ისევ {n} ზონამდე იხურება",
  "{k} zones open — one hides the keeper": "{k} ზონა ღიაა — ერთში მეკარე იმალება",
  "{pct}% goal": "გოლის შანსი {pct}%",
  "Next attack risks the whole pot — the goal resets to {n} zones.":
    "შემდეგი შეტევა მთელ ბანკს რისკავს — კარი ისევ {n} ზონამდე ბრუნდება.",

  // FIFA Cards
  "Guess the Card": "გამოიცანი ბარათი",
  "FIFA Cards": "FIFA ბარათები",
  "Spin for a FIFA edition, then name the player from the stats":
    "დაატრიალე FIFA-ს გამოცემა და სტატისტიკით გამოიცანი მოთამაშე",
  "Spinning the edition…": "ვირჩევთ გამოცემას…",
  "Locked in!": "დაფიქსირდა!",
  "Guess the player from the drawn edition": "გამოიცანი მოთამაშე ამოსული გამოცემიდან",
  "Card": "ბარათი",
  "No clues": "მინიშნების გარეშე",
  "{clue} unlocks in": "{clue} გამოჩნდება",
  "Final clue — answer now": "ბოლო მინიშნება — უპასუხე ახლა",
  "Name the player…": "დაასახელე მოთამაშე…",
  "Named on stats alone": "მხოლოდ სტატისტიკით გამოიცანი",
  "Guessed with {n} clue(s)": "გამოიცანი {n} მინიშნებით",
  "Round complete": "რაუნდი დასრულდა",
  "of {max} · {n}/{total} named": "{max}-დან · {n}/{total} გამოცნობილი",

  // Guess the Card — daily challenge
  "Card {n}/{total}": "ბარათი {n}/{total}",
  "Spin for a FIFA edition, then name the player from the card":
    "დაატრიალე FIFA-ს გამოცემა და გამოიცანი მოთამაშე ბარათით",
  "Give up": "დანებება",
  "Wrong!": "არასწორია!",
  // Difficulty labels (demo preview of the daily's balanced mix; "Easy"/"Hard" defined above)
  "Medium": "საშუალო",
  "Very hard": "ძალიან რთული",
  "Medium/Easy": "საშუალო/მარტივი",
  "Pre-FIFA 20": "FIFA 20-მდე",
  "Tap a lock to reveal a clue": "დააჭირე ბოქლომს მინიშნების გასახსნელად",
  "Clue revealed": "მინიშნება გახსნილია",
  "The answer": "პასუხი",
  "{clue} unlocks next": "შემდეგ იხსნება: {clue}",
  "Nice — +{p}": "ყოჩაღ — +{p}",
  "Daily complete!": "დღიური დასრულდა!",
  "Today's round is done": "დღევანდელი რაუნდი დასრულებულია",
  "Coins earned": "მოგებული ქოინები",
  "Solved": "ამოცნობილი",
  "New cards at reset": "ახალი ბარათები განახლებისას",
  "Come back tomorrow": "დაბრუნდი ხვალ",
  "Reveal clue": "მინიშნების გახსნა",
  "Hidden clue": "დამალული მინიშნება",
  "Back to challenges": "გამოწვევებში დაბრუნება",
  "A gold card, stats only — name the player across 10 editions.":
    "ოქროს ბარათი, მხოლოდ სტატისტიკა — გამოიცანი მოთამაშე 10 გამოცემიდან.",
  // Card Detective (daily + demo)
  "Tap a lock to buy that clue": "დააჭირე საკეტს მინიშნების საყიდლად",
  "Today {n} pts": "დღეს {n} ქულა",
  "Correct now = {n} pts": "სწორი ახლა = {n} ქულა",
  "Not {name} · −{n} coins": "არა, {name} · −{n} ქოინი",
  "Revealed": "გახსნილია",
  "Reveal {clue} for {n} coins": "გახსენი {clue} {n} ქოინად",
  "Hint budget": "მინიშნებების ბიუჯეტი",
  "Hint budget: {n} pts": "მინიშნებების ბიუჯეტი: {n} ქულა",
  "Hint budget resets every card · separate from your coins": "ბიუჯეტი ყოველ ბარათზე ახლდება · ეს შენი მონეტები არ არის",
  "Not {name} · −{n} pts": "არა, {name} · −{n} ქულა",
  "Reveal {clue} for {n} pts": "გახსენი {clue} {n} ქულად",
  "Buy clues on the card, name the player, keep your points": "იყიდე მინიშნებები ბარათზე, დაასახელე მოთამაშე, შეინარჩუნე ქულები",
  "Hints bought: {n}": "ნაყიდი მინიშნებები: {n}",
  "No hints bought": "მინიშნება არ გიყიდია",
  "+{n} pts saved by skipping hints": "+{n} ქულა დაზოგე მინიშნებების გარეშე",
  "No points on a surrendered card": "დანებებულ ბარათზე ქულა არ ითვლება",
  "Next card": "შემდეგი ბარათი",
  "See results": "შედეგების ნახვა",
  "{n} pts": "{n} ქულა",
  "Points saved through deduction": "დედუქციით დაზოგილი ქულები",
  "Across {n} solved cards": "{n} ამოცნობილ ბარათზე",
  "photo": "ფოტო",
  "nation": "ქვეყანა",
  "position": "პოზიცია",
  "club": "კლუბი",
  "league": "ლიგა",
  "POS": "პოზ",
  "NATION": "ქვეყანა",
  "LEAGUE": "ლიგა",
  "CLUB": "კლუბი",
  "Dealing…": "ბარათი რიგდება…",
  "Your card": "შენი ბარათი",
  "Buy clues on the card, name the player, keep your coins": "იყიდე მინიშნებები ბარათზე, დაასახელე მოთამაშე, შეინარჩუნე ქოინები",
  "Start": "დაწყება",
  "Run complete": "რაუნდი დასრულდა",
  "Rival": "მეტოქე",
};

const PT: Record<string, string> = {
  // Guess the Goal (live)
  "Guess the Goal": "Adivinhe o Gol",
  "Name the iconic goal — real rewards, server-scored": "Adivinhe o gol icônico — recompensas reais, pontuação pelo servidor",
  "Solved {n}/{total}": "Resolvidos {n}/{total}",
  "+{points} points": "+{points} pontos",
  "Move {n}/{total}": "Jogada {n}/{total}",
  "Which goal is this?": "Que gol é esse?",
  "Bonus question": "Pergunta bônus",
  "Next goal": "Próximo gol",
  "Guess the Goal is warming up — check back soon": "Adivinhe o Gol está chegando — volte em breve",
  "A legendary goal replays on the coaching board. The earlier you name it, the more you earn — first solve of each goal pays coins and XP.":
    "Um gol lendário é reprisado na prancheta tática. Quanto antes você adivinhar, mais ganha — acertar cada gol pela primeira vez rende moedas e XP.",
  "(daily cap)": "(limite diário)",
  "Name the legendary goal from the coaching board": "Adivinhe o gol lendário na prancheta tática",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal. Sign in to earn coins and XP.": "Um gol lendário é reprisado na prancheta tática. Veja as jogadas e adivinhe o gol. Entre para ganhar moedas e XP.",
  "Sign in to earn coins and XP for every goal you solve": "Entre para ganhar moedas e XP a cada gol que você acertar",
  "Already solved before — no repeat rewards": "Você já acertou — sem recompensas repetidas",
  "Something went wrong — try again": "Algo deu errado — tente de novo",
  "Watch the real goal": "Veja o gol real",
  "Back to the board": "Voltar à prancheta",
  "Board": "Prancheta",
  "Video": "Vídeo",
  "Replay board": "Rever a prancheta",
  "Couldn't load your game": "Não foi possível carregar sua partida",

  // Shared
  "Prototype — virtual points only, no real money or rewards": "Protótipo — apenas pontos virtuais, sem dinheiro ou recompensas reais",
  "Points": "Pontos",
  "Play again": "Jogue de novo",
  "Try again": "Tente de novo",
  "Next": "Próximo",
  "Back": "Voltar",
  "Opponent": "Adversário",

  // Accumulator
  "Accumulator": "Múltipla",
  "Five legs. One stake. All must land.": "Cinco palpites. Uma aposta. Todos precisam dar certo.",
  "Pick 5 legs · {n}/5": "Escolha 5 palpites · {n}/5",
  "Continue · {odds}x": "Continuar · {odds}x",
  "Potential return": "Retorno potencial",
  "Your stake": "Sua aposta",
  "Place bet · {stake}": "Aposte · {stake}",
  "Back to legs": "Voltar aos palpites",
  "Return so far": "Retorno até agora",
  "4 of 5 landed!": "4 de 5 deram certo!",
  "Take the money or go for it?": "Garanta o prêmio ou continue?",
  "One leg left. Cash out now, or risk it for the full {amount}.": "Falta um palpite. Resgate agora ou arrisque para ganhar {amount}.",
  "Cash out": "Resgatar",
  "Final leg": "Último palpite",
  "Bet won!": "Aposta ganha!",
  "Busted on leg {n}": "A aposta falhou no palpite {n}",
  "Lost your {stake} stake — one leg let it down.": "Você perdeu sua aposta de {stake} — um palpite estragou tudo.",
  "New accumulator": "Nova múltipla",
  "if it lands:": "se der certo:",
  "{leg}/{total} legs": "Palpites: {leg}/{total}",
  "Leg {n}": "Palpite {n}",

  // Bet Slip Booster
  "Answer right to boost your odds": "Acerte para aumentar as odds",
  "Bet Slip · Treble": "Bilhete · Tripla",
  "3 selections": "3 palpites",
  "boosted": "aumentado",
  "Total odds": "Odds totais",
  "Boost leg {n} · {club}": "Aumente o palpite {n} · {club}",
  "Boosted {a} → {b}": "Aumentou de {a} → {b}",
  "Stake {stake} · potential return": "Aposta {stake} · retorno potencial",
  "Slip landed!": "Bilhete premiado!",
  "Not this time": "Dessa vez não",
  "The boosted {odds}x didn't come in.": "As odds aumentadas de {odds}x não deram certo.",
  "New slip": "Novo bilhete",

  // Cash Out Ladder
  "Bank it or climb — one wrong answer wipes it all": "Resgate ou avance — um erro leva tudo",
  "Stake": "Aposta",
  "Wiped": "Perdido",
  "Banked": "Resgatado",
  "Pot at risk": "Prêmio em risco",
  "{mult}x on {stake}": "{mult}x sobre {stake}",
  "Stake {stake} & climb": "Aposte {stake} e avance",
  "Bank {pot} now, or risk it for {next}?": "Resgate {pot} agora ou arrisque para chegar a {next}?",
  "Bank {pot}": "Resgate {pot}",
  "Climb to {mult}x": "Avance até {mult}x",
  "Wiped!": "Perdeu tudo!",
  "Wrong answer at {mult}x — the whole pot is gone.": "Resposta errada em {mult}x — você perdeu todo o prêmio.",
  "Banked {amount}": "Resgatou {amount}",
  "😱 It would have hit 32x!": "😱 Você teria chegado a 32x!",
  "Smart! You'd have busted at {mult}x.": "Boa! Você teria perdido tudo em {mult}x.",
  "It would've climbed to {a}x then busted at {b}x.": "Teria chegado a {a}x e perdido tudo em {b}x.",
  "Go again": "Jogue de novo",

  // Daily Jackpot
  "One hard question. Winner takes the pot.": "Uma pergunta difícil. Quem ganhar leva o prêmio.",
  "Today's pot": "Prêmio de hoje",
  "climbing with every miss…": "aumenta a cada erro…",
  "failed today": "errou hoje",
  "next question": "próxima pergunta",
  "Hard · one attempt": "Difícil · uma tentativa",
  "Rolled over": "Acumulado",
  "Not this time — your entry fed the pot. It now sits at {pot}. Come back for the next question in {time}.": "Dessa vez não — sua entrada aumentou o prêmio, que agora está em {pot}. Volte para a próxima pergunta em {time}.",
  "Replay": "Jogar de novo",
  "Jackpot won!": "Prêmio máximo ganho!",
  "The pot resets for tomorrow's question.": "O prêmio reinicia para a pergunta de amanhã.",

  // Half-Time Trivia
  "Live": "Ao vivo",
  "Half-Time Quiz": "Quiz do intervalo",
  "Question {n} / {total}": "Pergunta {n} / {total}",
  "Get {n}+ for a free bet · {c} right": "Acerte {n}+ para ganhar uma aposta grátis · {c} certas",
  "Free bet won!": "Aposta grátis ganha!",
  "{c}/{total} — so close": "{c}/{total} — quase lá",
  "You earned a virtual second-half free bet (demo — no real rewards).": "Você ganhou uma aposta grátis virtual para o segundo tempo (demo — sem recompensas reais).",
  "Get {n}+ right before kickoff to earn a free bet.": "Acerte {n}+ antes do início da partida para ganhar uma aposta grátis.",
  "Markets": "Mercados",
  "Odds are illustrative · prototype": "Odds ilustrativas · protótipo",
  "Prototype — virtual only": "Protótipo — apenas virtual",

  // Odds Board
  "Odds Board": "Painel de odds",
  "Every answer is a market — back your call": "Cada resposta é um mercado — aposte no seu palpite",
  "Market {n}": "Mercado {n}",
  "{pct}% implied": "Probabilidade de {pct}%",
  "returns": "retorno",
  "Pick an answer to back": "Escolha uma resposta para apostar",
  "Place {stake} on {answer}": "Aposte {stake} em {answer}",
  "Won {amount}!": "Ganhou {amount}!",
  "Lost {amount}": "Perdeu {amount}",
  "Big-odds call!": "Palpite de odds altas!",
  "Right answer:": "Resposta certa:",
  "Next market": "Próximo mercado",

  // Pass Chain
  "Pass Chain": "Conexão entre jogadores",
  "Link the two players through shared clubs": "Conecte os dois jogadores por clubes em comum",
  "Fewer links score higher": "Menos conexões valem mais pontos",
  "Par —": "Par —",
  "Par {par} links": "Par de {par} conexões",
  "Who links to {name}?": "Quem se conecta a {name}?",
  "Add": "Adicionar",
  "Already in the chain": "Já está na sequência",
  "Unknown player — try another": "Jogador desconhecido — tente outro",
  "{a} never played with {b}": "{a} nunca jogou com {b}",
  "Reset": "Reiniciar",
  "Linked!": "Conectado!",
  "{n} links": "Conexões: {n}",
  "Par — perfect!": "Par — perfeito!",
  "par {n}": "par {n}",
  "Next chain": "Próxima sequência",
  "START": "INÍCIO",
  "TARGET": "ALVO",

  // Penalty Shootout
  "Penalty Shootout": "Disputa de pênaltis",
  "Round {n} / {total}": "Rodada {n} / {total}",
  "You · AI": "Você · IA",
  "Answer to earn your shot": "Responda para ganhar sua cobrança",
  "Opponent is stepping up…": "O adversário vai cobrar…",
  "AI is on target — get ready to dive!": "A IA vai cobrar no gol — prepare-se para pular!",
  "AI answering…": "A IA está respondendo…",
  "Pick your corner — beat the keeper": "Escolha o canto — vença o goleiro",
  "You're in goal — pick your dive!": "Você está no gol — escolha onde pular!",
  "GOAL!": "GOL!",
  "MISSED": "PARA FORA",
  "AI SCORES": "IA MARCA",
  "SAVED!": "DEFENDEU!",
  "Opponent's turn": "Vez do adversário",
  "See result": "Ver resultado",
  "Next round": "Próxima rodada",
  "You win!": "Você ganhou!",
  "Draw": "Empate",
  "You lose": "Você perdeu",

  // Free Kicks (formerly Final Third)
  "Free Kicks": "Cobranças de falta",
  "Know football. Read the goal. Take the shot.": "Entenda de futebol. Leia o gol. Faça a cobrança.",
  "Balance": "Saldo",
  "Pot": "Prêmio",
  "Your pot": "Seu prêmio",
  "Stake {n} & attack": "Aposte {n} e ataque",
  "Min {n}": "Mín. {n}",
  "Top up (demo)": "Adicionar saldo (demo)",
  "Answer to scout the keeper": "Responda para analisar o goleiro",
  "{n}s": "{n}s",
  "Time's up — blind shot": "O tempo acabou — cobrança no escuro",
  "Wrong — the correct answer stays. Blind shot.": "Errado — a resposta certa continua. Cobrança no escuro.",
  "Answer to scout the keeper — knowledge sharpens the shot, the risk stays.": "Responda para analisar o goleiro — conhecimento melhora a cobrança, mas o risco continua.",
  "Attack {n}": "Ataque {n}",
  "SCOUT REPORT ✓ — one save zone revealed": "ANÁLISE ✓ — uma área de defesa revelada",
  "No scout — blind shot pays more": "Sem análise — cobrança no escuro paga mais",
  "Pick your shot zone": "Escolha onde chutar",
  "4 GOAL · 2 SAVE": "4 GOLS · 2 DEFESAS",
  "4 GOAL · 1 SAVE": "4 GOLS · 1 DEFESA",
  "TAKE {amount}": "RESGATE {amount}",
  "NEXT ATTACK": "PRÓXIMO ATAQUE",
  "Next attack risks the whole pot — the keeper resets.": "O próximo ataque coloca todo o prêmio em risco — o goleiro volta ao início.",
  "The keeper read it — pot lost.": "O goleiro leu a cobrança — prêmio perdido.",
  "Cashed out {amount}!": "Resgatou {amount}!",
  "Loading…": "Carregando…",
  "Could not load balance — tap to retry": "Não foi possível carregar o saldo — toque para tentar de novo",
  "New round": "Nova rodada",
  "Live wins": "Vitórias ao vivo",
  "Longest runs today": "Maiores sequências de hoje",
  "{n} playing now": "Jogadores em campo agora: {n}",
  "{pct}% answered correctly": "{pct}% responderam corretamente",
  "{pct}% scored": "{pct}% marcaram",
  "{pct}% cashed out": "{pct}% resgataram",
  "{n} going NEXT ATTACK": "Jogadores no PRÓXIMO ATAQUE: {n}",
  "You": "Você",
  "just now": "agora mesmo",
  "{n}m ago": "há {n} min",

  // Squad Collection
  "Squad Collection": "Coleção do time",
  "Answer to pull cards. Build your XI.": "Responda para ganhar cartas. Monte seu time.",
  "Squad": "Time",
  "Answer to earn a pack": "Responda para ganhar um pacote",
  "Tap to open": "Toque para abrir",
  "Add to squad": "Adicionar ao time",
  "Squad complete!": "Time completo!",
  "Reward: +2,000 🪙": "Recompensa: +2.000 🪙",
  "Start a new squad": "Monte um novo time",
  "Bronze": "Bronze",
  "Silver": "Prata",
  "Gold": "Ouro",
  "Special": "Especial",

  // Squad Spin
  "Easy": "Fácil",
  "Hard": "Difícil",
  "Expert": "Especialista",
  "Name a player who fits every reel": "Diga o nome de um jogador que combine com cada rolo",
  "{n} reels": "Rolos: {n}",
  "Spin": "Girar",
  "Spinning…": "Girando…",
  "Name a player…": "Diga o nome de um jogador…",
  "Go": "Valendo",
  "Hold a reel, respin the rest": "Trave um rolo e gire os outros de novo",
  "Respin −{n}": "Girar de novo −{n}",
  "Last 3 spins": "Últimas 3 rodadas",
  "Missed": "Errou",
  "Correct!": "Correto!",
  "Only {pct}% named this": "Só {pct}% acertaram",
  "rarity": "raridade",
  "Time's up": "O tempo acabou",
  "You could've said:": "Você poderia ter dito:",
  "No valid player for that combo!": "Nenhum jogador válido para essa combinação!",
  "Spin again": "Gire de novo",
  "Hold reel": "Travar rolo",
  "Release reel": "Destravar rolo",
  "Club": "Clube",
  "Position": "Posição",
  "Nation": "País",
  "Era": "Época",
  "Trophy": "Título",

  // Trivia Spin
  "easy": "fácil",
  "medium": "médio",
  "hard": "difícil",
  "Answer to earn spins — the wheel pays out": "Responda para ganhar rodadas — a roleta dá prêmios",
  "Coins": "Moedas",
  "Question {n}": "Pergunta {n}",
  "+1 spin earned": "+1 rodada ganha",
  "No spin — try the next one": "Sem rodada — tente a próxima pergunta",
  "{n} spins available": "Rodadas disponíveis: {n}",
  "JACKPOT!": "PRÊMIO MÁXIMO!",
  "Spin the wheel": "Gire a roleta",
  "Answer to earn a spin": "Responda para ganhar uma rodada",

  // Data labels (fixtures / bet slip picks)
  "Full Time Result": "Resultado final",
  "Next Goal": "Próximo gol",
  "Total Goals Over/Under 3.5": "Total de gols: acima/abaixo de 3,5",
  "Both Teams to Score — 2nd Half": "Ambos os times marcam — 2º tempo",
  "No more goals": "Sem mais gols",
  "Over 3.5": "Mais de 3,5",
  "Under 3.5": "Menos de 3,5",
  "Yes": "Sim",
  "No": "Não",
  "Champions League · Group Stage": "Champions League · Fase de grupos",
  "Real Madrid to win": "Vitória do Real Madrid",
  "Man City to win": "Vitória do Man City",
  "Bayern to win": "Vitória do Bayern de Munique",
  "Goalkeeper": "Goleiro",
  "Zone": "Área",
  "Defender": "Zagueiro",
  "Midfielder": "Meio-campista",
  "Forward": "Atacante",
  "World Cup": "Copa do Mundo",
  "League": "Liga",
  "Champions League": "Champions League",
  "League Title": "Título da liga",

  // Football Tic Tac Toe
  "Football Tic Tac Toe": "Jogo da Velha do Futebol",
  "Claim cells with players — three in a row wins": "Escolha jogadores para ocupar as casas — três em linha vencem",
  "You · Opponent": "Você · Adversário",
  "Pick a cell and name a player who played for that club AND that nation. Your opponent answers back — line up three to win.":
    "Escolha uma casa e diga o nome de um jogador que atuou por aquele clube E por aquela seleção. Seu adversário responde — alinhe três para vencer.",
  "Start match": "Começar partida",
  "Your turn — pick a cell": "Sua vez — escolha uma casa",
  "Not a match — turn passes": "Não vale — a vez passa",
  "Time's up — turn passes": "O tempo acabou — a vez passa",
  "Pass": "Passe",
  "Opponent blanked — your turn!": "O adversário não lembrou — sua vez!",
  "Opponent is thinking…": "O adversário está pensando…",
  "Opponent wins": "O adversário venceu",
  "Three in a row!": "Três em linha!",
  "Board full — {a} cells vs {b}": "Tabuleiro completo — {a} casas contra {b}",
  "New grid": "Novo tabuleiro",
  "{pct}%": "{pct}%",
  "Brazil": "Brasil",
  "France": "França",
  "Argentina": "Argentina",
  "Spain": "Espanha",
  "Netherlands": "Países Baixos",
  "Portugal": "Portugal",

  // Survivor
  "Survivor": "Sobrevivente",
  "One wrong answer and you are out — how long can you last?": "Errou uma resposta, está fora — até onde você consegue chegar?",
  "Streak": "Sequência",
  "Sudden death": "Morte súbita",
  "Questions get harder as you go. {n} virtual players start with you — outlast them all.": "As perguntas ficam mais difíceis. {n} jogadores virtuais começam com você — sobreviva a todos.",
  "Best streak: {n}": "Melhor sequência: {n}",
  "Enter the arena": "Entre na arena",
  "Still standing": "Ainda na disputa",
  "Eliminated": "Eliminado",
  "Streak of {n}": "Sequência: {n}",
  "You outlasted {pct}% of the field — {left} were still standing.": "Você resistiu mais que {pct}% dos jogadores — {left} continuavam na disputa.",
  "New best!": "Novo recorde!",
  "Run it back": "Jogue de novo",
  "Last one standing!": "Você foi o último em pé!",
  "You cleared every question in the bank — a perfect {n}-answer run.": "Você acertou todas as perguntas disponíveis — uma sequência perfeita de {n} respostas.",

  // Hi-Lo Ride
  "Hi-Lo Ride": "Maior ou menor",
  "Call the higher stat — odds priced by how many get it right": "Adivinhe o maior número — as odds dependem de quantos acertam",
  "Chain higher-or-lower calls. Every correct call multiplies your stake — hard matchups pay more. Cash out before you miss.":
    "Acerte uma sequência de maior ou menor. Cada acerto multiplica sua aposta — disputas difíceis pagam mais. Resgate antes de errar.",
  "Stake {stake} & ride": "Aposte {stake} e continue",
  "Step {n} · odds": "Etapa {n} · odds",
  "{pct}% of players call this one right": "{pct}% dos jogadores acertam",
  "Which is higher?": "Qual é maior?",
  "Higher": "Maior",
  "Pot is {pot}. Next matchup pays {odds}x → {next}.": "O prêmio é {pot}. A próxima disputa paga {odds}x → {next}.",
  "Cash out {pot}": "Resgatar {pot}",
  "Ride on": "Continuar",
  "Wrong call": "Resposta errada",
  "The ride ends — your {stake} stake is gone.": "A sequência acabou — você perdeu sua aposta de {stake}.",
  "New ride": "Nova sequência",
  "Cashed {amount}!": "Resgatou {amount}!",
  "{n} correct calls — {mult}x your stake.": "Acertos: {n} — {mult}x sua aposta.",

  // Trivia Mines
  "Trivia Mines": "Campo minado de perguntas",
  "Dribble past hidden defenders — scout them with your knowledge": "Drible pelos zagueiros escondidos — use seu conhecimento para encontrá-los",
  "{d} defenders hide in {n} tiles. Every clean dribble grows the pot — hit a defender and lose it all. Answer questions to scout defenders out.":
    "Há {d} zagueiros escondidos em {n} casas. Cada drible limpo aumenta o prêmio — se encontrar um zagueiro, perde tudo. Responda às perguntas para localizá-los.",
  "Stake {stake} & dribble": "Aposte {stake} e drible",
  "Tackled": "Desarmado",
  "Now · next tile": "Agora · próxima casa",
  "Scout ({n} left)": "Analisar ({n} restantes)",
  "{n} safe tiles opened · {d} defenders in play": "Casas seguras abertas: {n} · zagueiros em jogo: {d}",
  "Scout report — a defender is marked!": "Análise — um zagueiro foi localizado!",
  "Scout failed — no intel": "Análise falhou — nenhuma informação",
  "Answer to scout a defender": "Responda para localizar um zagueiro",
  "Tackled!": "Desarmado!",
  "A defender got you after {n} clean tiles — the pot is gone.": "Um zagueiro pegou você após {n} casas seguras — você perdeu o prêmio.",
  "New run": "Nova partida",
  "{n} clean dribbles at {mult}x.": "Dribles limpos: {n} · {mult}x.",

  // Quiz Board
  "Quiz Board": "Tabuleiro de perguntas",
  "Pick tiles, bank the value — steal when the AI slips": "Escolha casas e acumule pontos — roube quando a IA errar",
  "Nine tiles, three value tiers. Answer to bank the tile and keep control — miss and the AI can steal. Highest bank wins.": "Nove casas, três valores. Acerte para ganhar os pontos e manter o controle — erre e a IA pode roubar. Quem somar mais vence.",
  "Start game": "Começar jogo",
  "Clubs": "Clubes",
  "Legends": "Lendas",
  "Tournaments": "Torneios",
  "Your board — pick a tile": "Seu tabuleiro — escolha uma casa",
  "AI is picking a tile…": "A IA está escolhendo uma casa…",
  "AI plays for {v}": "A IA joga por {v}",
  "AI banks it": "A IA garantiu os pontos",
  "AI is wrong — steal it!": "A IA errou — roube os pontos!",
  "STEAL for {v}!": "ROUBE {v}!",
  "For {v}": "Vale {v}",
  "Final banks — you {a}, AI {b}.": "Pontuação final — você {a}, IA {b}.",
  "New board": "Novo tabuleiro",

  // Last One Standing
  "Last One Standing": "Último em pé",
  "{n} enter, one survives — make every cut": "{n} começam, um sobrevive — passe por cada rodada",
  "Alive": "Na disputa",
  "Battle royale quiz": "Quiz battle royale",
  "Every round the field is cut — answer right and fast to stay in it. Nine rounds from {n} players down to one.": "A cada rodada, jogadores são eliminados — acerte rápido para continuar. São nove rodadas, de {n} jogadores até sobrar um.",
  "Kick off": "Começar",
  "Continue": "Continuar",
  "A legendary goal replays on the coaching board. Name it — the first solve of each goal pays coins and XP.": "Um gol lendário é reprisado na prancheta tática. Adivinhe — acertar cada gol pela primeira vez rende moedas e XP.",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal.": "Um gol lendário é reprisado na prancheta tática. Veja as jogadas e adivinhe o gol.",
  "Back tomorrow": "Volte amanhã",
  "All {limit} goals played — come back tomorrow": "Você jogou todos os {limit} gols — volte amanhã",
  "{left} of {limit} goals left today": "Gols restantes hoje: {left} de {limit}",
  "Daily coins earned — more tomorrow": "Você já ganhou as moedas de hoje — tem mais amanhã",
  "Today: {today}/{cap} coins": "Hoje: {today}/{cap} moedas",
  "Your collection": "Sua coleção",
  "Fastest {cap} survive": "Os {cap} mais rápidos sobrevivem",
  "Field": "Grupo",
  "You made the cut!": "Você passou de fase!",
  "#{rank} fastest — {cap} players remain.": "Você foi o #{rank} mais rápido — restam {cap} jogadores.",
  "Cut!": "Eliminado!",
  "Finished #{place} of {n}": "Você ficou em #{place} de {n}",
  "Round {r} was your last — the field marched on without you.": "A rodada {r} foi sua última — os outros continuaram sem você.",
  "Re-enter": "Voltar à disputa",
  "CHAMPION!": "CAMPEÃO!",
  "{n} started. You are the last one standing.": "{n} começaram. Você foi o último em pé.",
  "Defend the title": "Defenda o título",

  // Golden Goal
  "Golden Goal": "Gol de Ouro",
  "Sudden death — push the ball into their net": "Morte súbita — coloque a bola no gol adversário",
  "Round": "Rodada",
  "Both of you answer the same question — faster and righter pushes the ball. Reach ±{n} and it is a goal. First goal wins.": "Vocês respondem à mesma pergunta — quem acertar mais rápido avança com a bola. Chegue a ±{n} para marcar. O primeiro gol vence.",
  "AI net": "Gol da IA",
  "Your attack": "Seu ataque",
  "GOLDEN GOAL!": "GOL DE OURO!",
  "AI scores": "A IA marcou",
  "Decided in {n} rounds.": "Decidido em {n} rodadas.",
  "Rematch": "Revanche",
  "vs": "vs",

  // Career Race
  "Career Race": "Adivinhe a carreira",
  "The transfer trail reveals — buzz before your rival": "A trajetória no futebol vai aparecer — responda antes do adversário",
  "Clubs appear one by one. Buzz early for 100 points — every extra club cuts the prize. Wrong buzz and you are out of the round. Best of {n}.": "Os clubes aparecem um a um. Responda cedo para ganhar 100 pontos — cada clube revelado reduz o prêmio. Se errar, você sai da rodada. Melhor de {n}.",
  "Start race": "Começar disputa",
  "Buzz now: {pts} pts": "Responda agora: {pts} pts",
  "Locked out": "Bloqueado",
  "BUZZ!": "RESPONDEU!",
  "Rival buzzed wrong — they are out!": "O adversário errou — está fora!",
  "Your rival is watching the same trail…": "Seu adversário está vendo a mesma trajetória…",
  "Whose career is this?": "De quem é essa carreira?",
  "Wrong — locked out!": "Errado — você está fora!",
  "⚡ Rival buzzes…": "⚡ O adversário respondeu…",
  "+{pts} — yours!": "+{pts} — é seu!",
  "Rival takes {pts}": "O adversário ganhou {pts}",
  "Nobody got it": "Ninguém acertou",
  "It was {name}": "Era {name}",
  "You win the race!": "Você venceu a disputa!",
  "Rival wins": "O adversário venceu",
  "Race again": "Dispute de novo",

  // Stat Sniper
  "Closest Wins": "Quem chega mais perto vence",
  "No options, no help — land your guess on the number": "Sem opções nem ajuda — tente acertar o número",
  "Score": "Pontuação",
  "{n} numeric stats — slide to your best guess. The closer you land, the more you score; a perfect hit pays a bullseye bonus.": "Adivinhe {n} números — deslize até seu melhor palpite. Quanto mais perto, mais pontos; acertar em cheio dá bônus.",
  "Take aim": "Mire",
  "Target {n} / {total}": "Alvo {n} / {total}",
  "Lock it in": "Confirmar",
  "Answer": "Resposta",
  "BULLSEYE! +{pts}": "NA MOSCA! +{pts}",
  "+{pts} pts": "+{pts} pts",
  "You were {diff} off.": "Você errou por {diff}.",
  "Next target": "Próximo alvo",
  "Elite sniper!": "Atirador de elite!",
  "Sharp shooter": "Boa pontaria",
  "On the range": "No alvo",
  "Blindfolded": "De olhos vendados",
  "{score} points over {n} targets": "{score} pontos em {n} alvos",
  "Reload": "Recarregar",

  // Free Kicks — boost rules (50/50 shot, answers raise the payout)
  "Every shot is a 50/50 duel with the keeper. Answer questions to boost the payout — a wrong answer resets the boost.": "Cada cobrança é um duelo 50/50 com o goleiro. Responda às perguntas para aumentar o prêmio — uma resposta errada zera o bônus.",
  "Boost {a}/{max} · ×{mult}": "Bônus {a}/{max} · ×{mult}",
  "BOOSTED! Payout is now ×{mult}": "BÔNUS ATIVO! O prêmio agora é ×{mult}",
  "Wrong — boost resets to ×{mult}": "Errado — o bônus volta para ×{mult}",
  "50/50 shot · current payout ×{mult}": "Cobrança 50/50 · prêmio atual ×{mult}",
  "Answer · boost to ×{next}": "Responda · aumente para ×{next}",
  "Boosts raise the payout — the shot stays 50/50.": "Os bônus aumentam o prêmio — a cobrança continua 50/50.",
  "Boosting…": "Aumentando…",
  "Time's up — boost resets to ×{mult}": "O tempo acabou — o bônus volta para ×{mult}",
  "Left or right — the keeper picks one": "Esquerda ou direita — o goleiro escolhe um lado",
  "Pick your corner": "Escolha o canto",
  "Next attack risks the whole pot — the boost resets.": "O próximo ataque coloca todo o prêmio em risco — o bônus zera.",

  // Free Kicks — run-multiplier HUD
  "Run ×{run} → ×{next}": "Sequência ×{run} → ×{next}",
  "Run": "Sequência",
  "Answering is locked this attack — take the shot.": "Não dá para responder neste ataque — faça a cobrança.",
  "Sound settings": "Configurações de som",
  "Crowd": "Torcida",
  "Effects": "Efeitos",
  "Off": "Desligado",
  "More open zones = better odds of keeping the run alive.": "Mais áreas abertas = mais chances de manter a sequência.",

  // Free Kicks — Option C (open-zones) rules
  "The goal opens with 2 zones and one hidden keeper. Answer questions to open up to 6 — a wrong answer slams it back to 2.": "O gol começa com 2 áreas e um goleiro escondido. Responda para abrir até 6 — se errar, volta para 2.",
  "{k} open · {pct}% · ×{mult}": "{k} abertas · {pct}% · ×{mult}",
  "Zone opened! {k} of {max} in play": "Área aberta! {k} de {max} em jogo",
  "Pick your angle and shoot · {pot} → {next}": "Escolha o canto e chute · {pot} → {next}",
  "Wrong — goal slams back to {n} zones": "Errado — o gol volta a ter {n} áreas",
  "{k} zones open · 1 keeper hidden · {pct}% goal": "Áreas abertas: {k} · goleiro escondido: 1 · chance de gol: {pct}%",
  "Answer · open zone {n}": "Responda · abra a área {n}",
  "Shoot · {pot} → {next}": "Chute · {pot} → {next}",
  "More zones = safer shot, smaller payout — the maths favours knowing.": "Mais áreas = cobrança mais segura e prêmio menor — conhecimento faz a diferença.",
  "Opening the goal…": "Abrindo o gol…",
  "Read the question…": "Leia a pergunta…",
  "Time's up — the goal slams back to {n} zones": "O tempo acabou — o gol volta a ter {n} áreas",
  "Wrong — the goal slams back to {n} zones": "Errado — o gol volta a ter {n} áreas",
  "{k} zones open — one hides the keeper": "Áreas abertas: {k} — o goleiro está escondido em uma delas",
  "{pct}% goal": "Chance de gol: {pct}%",
  "Next attack risks the whole pot — the goal resets to {n} zones.": "O próximo ataque coloca todo o prêmio em risco — o gol volta a ter {n} áreas.",

  // FIFA Cards
  "Guess the Card": "Adivinhe a Carta",
  "FIFA Cards": "Cartas FIFA",
  "Spin for a FIFA edition, then name the player from the stats": "Sorteie uma edição do FIFA e adivinhe o jogador pelas estatísticas",
  "Spinning the edition…": "Sorteando a edição…",
  "Locked in!": "Confirmado!",
  "Guess the player from the drawn edition": "Adivinhe o jogador pela edição sorteada",
  "Card": "Carta",
  "No clues": "Sem dicas",
  "{clue} unlocks in": "A dica {clue} será liberada em",
  "Final clue — answer now": "Última dica — responda agora",
  "Name the player…": "Diga o nome do jogador…",
  "Named on stats alone": "Acertou só pelas estatísticas",
  "Guessed with {n} clue(s)": "Acertou com {n} dicas",
  "Round complete": "Rodada concluída",
  "of {max} · {n}/{total} named": "de {max} · {n}/{total} acertados",

  // Guess the Card — daily challenge
  "Card {n}/{total}": "Carta {n}/{total}",
  "Spin for a FIFA edition, then name the player from the card": "Sorteie uma edição do FIFA e adivinhe o jogador pela carta",
  "Give up": "Desistir",
  "Wrong!": "Errado!",
  // Difficulty labels
  "Medium": "Médio",
  "Very hard": "Muito difícil",
  "Medium/Easy": "Médio/Fácil",
  "Pre-FIFA 20": "Antes do FIFA 20",
  "Tap a lock to reveal a clue": "Toque no cadeado para revelar uma dica",
  "Clue revealed": "Dica revelada",
  "The answer": "A resposta",
  "{clue} unlocks next": "Próxima dica: {clue}",
  "Nice — +{p}": "Boa — +{p}",
  "Daily complete!": "Desafio diário concluído!",
  "Today's round is done": "A rodada de hoje terminou",
  "Coins earned": "Moedas ganhas",
  "Solved": "Acertou",
  "New cards at reset": "Novas cartas na próxima atualização",
  "Come back tomorrow": "Volte amanhã",
  "Reveal clue": "Revelar dica",
  "Hidden clue": "Dica escondida",
  "Back to challenges": "Voltar aos desafios",
  "A gold card, stats only — name the player across 10 editions.": "Uma carta ouro, só com estatísticas — adivinhe o jogador em 10 edições.",

  // Card Detective (daily + demo)
  "Tap a lock to buy that clue": "Toque no cadeado para comprar a dica",
  "Today {n} pts": "Hoje: {n} pts",
  "Correct now = {n} pts": "Acerte agora = {n} pts",
  "Not {name} · −{n} coins": "Não é {name} · −{n} moedas",
  "Revealed": "Revelada",
  "Reveal {clue} for {n} coins": "Revele {clue} por {n} moedas",
  "Hint budget": "Orçamento de dicas",
  "Hint budget: {n} pts": "Orçamento de dicas: {n} pts",
  "Hint budget resets every card · separate from your coins": "O orçamento reinicia a cada carta · não são suas moedas",
  "Not {name} · −{n} pts": "Não é {name} · −{n} pts",
  "Reveal {clue} for {n} pts": "Revele {clue} por {n} pts",
  "Buy clues on the card, name the player, keep your points": "Compre dicas da carta, adivinhe o jogador e guarde seus pontos",
  "Hints bought: {n}": "Dicas compradas: {n}",
  "No hints bought": "Nenhuma dica comprada",
  "+{n} pts saved by skipping hints": "+{n} pts economizados sem dicas",
  "No points on a surrendered card": "Carta abandonada não vale pontos",
  "Next card": "Próxima carta",
  "See results": "Ver resultados",
  "{n} pts": "{n} pts",
  "Points saved through deduction": "Pontos economizados na dedução",
  "Across {n} solved cards": "Em {n} cartas acertadas",
  "photo": "foto",
  "nation": "seleção",
  "position": "posição",
  "club": "clube",
  "league": "liga",
  "POS": "POS",
  "NATION": "SELEÇÃO",
  "LEAGUE": "LIGA",
  "CLUB": "CLUBE",
  "Dealing…": "Distribuindo…",
  "Your card": "Sua carta",
  "Buy clues on the card, name the player, keep your coins": "Compre dicas da carta, adivinhe o jogador e guarde suas moedas",
  "Start": "Começar",
  "Run complete": "Partida concluída",
  "Rival": "Rival",
};

const DE: Record<string, string> = {
  // Guess the Goal (live)
  "Guess the Goal": "Tor erraten",
  "Name the iconic goal — real rewards, server-scored": "Errate das legendäre Tor — echte Belohnungen, serverseitig gewertet",
  "Solved {n}/{total}": "Erraten: {n}/{total}",
  "+{points} points": "+{points} Punkte",
  "Move {n}/{total}": "Zug {n}/{total}",
  "Which goal is this?": "Welches Tor ist das?",
  "Bonus question": "Bonusfrage",
  "Next goal": "Nächstes Tor",
  "Guess the Goal is warming up — check back soon": "Errate das Tor startet bald — schau wieder vorbei",
  "A legendary goal replays on the coaching board. The earlier you name it, the more you earn — first solve of each goal pays coins and XP.": "Ein legendäres Tor läuft auf der Taktiktafel erneut. Je früher du es errätst, desto mehr erhältst du — für jedes erstmals erratene Tor gibt es Münzen und XP.",
  "(daily cap)": "(Tageslimit)",
  "Name the legendary goal from the coaching board": "Errate das legendäre Tor auf der Taktiktafel",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal. Sign in to earn coins and XP.": "Ein legendäres Tor läuft auf der Taktiktafel erneut. Beobachte die Spielzüge und errate das Tor. Melde dich an, um Münzen und XP zu erhalten.",
  "Sign in to earn coins and XP for every goal you solve": "Melde dich an und erhalte für jedes erratene Tor Münzen und XP",
  "Already solved before — no repeat rewards": "Schon erraten — keine weitere Belohnung",
  "Something went wrong — try again": "Etwas ist schiefgelaufen — versuch es noch einmal",
  "Watch the real goal": "Sieh dir das echte Tor an",
  "Back to the board": "Zurück zur Tafel",
  "Board": "Tafel",
  "Video": "Video",
  "Replay board": "Tafel erneut ansehen",
  "Couldn't load your game": "Dein Spiel konnte nicht geladen werden",

  // Shared
  "Prototype — virtual points only, no real money or rewards": "Prototyp — nur virtuelle Punkte, kein echtes Geld und keine echten Belohnungen",
  "Points": "Punkte",
  "Play again": "Noch einmal spielen",
  "Try again": "Noch einmal versuchen",
  "Next": "Weiter",
  "Back": "Zurück",
  "Opponent": "Gegner",

  // Accumulator
  "Accumulator": "Kombination",
  "Five legs. One stake. All must land.": "Fünf Auswahlen. Ein Betrag. Alle müssen stimmen.",
  "Pick 5 legs · {n}/5": "Wähle 5 Auswahlen · {n}/5",
  "Continue · {odds}x": "Weiter · {odds}x",
  "Potential return": "Mögliche Belohnung",
  "Your stake": "Dein Betrag",
  "Place bet · {stake}": "Betrag wählen · {stake}",
  "Back to legs": "Zurück zur Auswahl",
  "Return so far": "Bisherige Belohnung",
  "4 of 5 landed!": "4 von 5 richtig!",
  "Take the money or go for it?": "Sichern oder weiterspielen?",
  "One leg left. Cash out now, or risk it for the full {amount}.": "Noch eine Auswahl. Sichere jetzt deine Münzen oder spiele um die vollen {amount}.",
  "Cash out": "Sichern",
  "Final leg": "Letzte Auswahl",
  "Bet won!": "Alles richtig!",
  "Busted on leg {n}": "Bei Auswahl {n} ausgeschieden",
  "Lost your {stake} stake — one leg let it down.": "Deine {stake} Münzen sind weg — eine Auswahl hat nicht gestimmt.",
  "New accumulator": "Neue Kombination",
  "if it lands:": "wenn es klappt:",
  "{leg}/{total} legs": "Auswahlen: {leg}/{total}",
  "Leg {n}": "Auswahl {n}",

  // Bet Slip Booster
  "Answer right to boost your odds": "Antworte richtig und erhöhe den Multiplikator",
  "Bet Slip · Treble": "Auswahl · Dreierkombi",
  "3 selections": "3 Auswahlen",
  "boosted": "erhöht",
  "Total odds": "Gesamtmultiplikator",
  "Boost leg {n} · {club}": "Erhöhe Auswahl {n} · {club}",
  "Boosted {a} → {b}": "Erhöht: {a} → {b}",
  "Stake {stake} · potential return": "Betrag {stake} · mögliche Belohnung",
  "Slip landed!": "Alles richtig!",
  "Not this time": "Diesmal nicht",
  "The boosted {odds}x didn't come in.": "Der erhöhte Multiplikator von {odds}x hat nicht geklappt.",
  "New slip": "Neue Auswahl",

  // Cash Out Ladder
  "Bank it or climb — one wrong answer wipes it all": "Sichere deine Münzen oder spiel weiter — eine falsche Antwort kostet alles",
  "Stake": "Betrag",
  "Wiped": "Verloren",
  "Banked": "Gesichert",
  "Pot at risk": "Münzen in Gefahr",
  "{mult}x on {stake}": "{mult}x auf {stake}",
  "Stake {stake} & climb": "Spiele um {stake} weiter",
  "Bank {pot} now, or risk it for {next}?": "Sichere jetzt {pot} oder spiele um {next} weiter?",
  "Bank {pot}": "{pot} sichern",
  "Climb to {mult}x": "Weiter auf {mult}x",
  "Wiped!": "Alles verloren!",
  "Wrong answer at {mult}x — the whole pot is gone.": "Falsche Antwort bei {mult}x — alle Münzen sind weg.",
  "Banked {amount}": "{amount} gesichert",
  "😱 It would have hit 32x!": "😱 Es wären 32x geworden!",
  "Smart! You'd have busted at {mult}x.": "Gut gespielt! Bei {mult}x wärst du ausgeschieden.",
  "It would've climbed to {a}x then busted at {b}x.": "Es wäre auf {a}x gestiegen und bei {b}x vorbei gewesen.",
  "Go again": "Noch einmal",

  // Daily Jackpot
  "One hard question. Winner takes the pot.": "Eine schwere Frage. Wer richtig liegt, erhält alle Münzen.",
  "Today's pot": "Münzen heute",
  "climbing with every miss…": "steigt mit jedem Fehler …",
  "failed today": "heute falsch",
  "next question": "Nächste Frage",
  "Hard · one attempt": "Schwer · ein Versuch",
  "Rolled over": "Übertragen",
  "Not this time — your entry fed the pot. It now sits at {pot}. Come back for the next question in {time}.": "Diesmal nicht — dein Beitrag hat den Topf erhöht. Er enthält jetzt {pot}. Komm in {time} zur nächsten Frage zurück.",
  "Replay": "Noch einmal spielen",
  "Jackpot won!": "Jackpot geknackt!",
  "The pot resets for tomorrow's question.": "Der Topf wird für die morgige Frage zurückgesetzt.",

  // Half-Time Trivia
  "Live": "Live",
  "Half-Time Quiz": "Halbzeit-Quiz",
  "Question {n} / {total}": "Frage {n} / {total}",
  "Get {n}+ for a free bet · {c} right": "Mindestens {n} richtig für einen Gratisversuch · {c} richtig",
  "Free bet won!": "Gratisversuch gewonnen!",
  "{c}/{total} — so close": "{c}/{total} — knapp daneben",
  "You earned a virtual second-half free bet (demo — no real rewards).": "Du hast einen virtuellen Gratisversuch für die zweite Halbzeit erhalten (Demo — keine echten Belohnungen).",
  "Get {n}+ right before kickoff to earn a free bet.": "Beantworte vor dem Anpfiff mindestens {n} Fragen richtig und erhalte einen Gratisversuch.",
  "Markets": "Auswahlen",
  "Odds are illustrative · prototype": "Multiplikatoren nur zur Veranschaulichung · Prototyp",
  "Prototype — virtual only": "Prototyp — nur virtuell",

  // Odds Board
  "Odds Board": "Multiplikator-Tafel",
  "Every answer is a market — back your call": "Jede Antwort ist eine Auswahl — entscheide dich",
  "Market {n}": "Auswahl {n}",
  "{pct}% implied": "{pct}% Wahrscheinlichkeit",
  "returns": "Belohnung",
  "Pick an answer to back": "Wähle eine Antwort",
  "Place {stake} on {answer}": "Wähle {answer} · {stake}",
  "Won {amount}!": "Erhalten: {amount}!",
  "Lost {amount}": "{amount} verloren",
  "Big-odds call!": "Hoher Multiplikator!",
  "Right answer:": "Richtige Antwort:",
  "Next market": "Nächste Auswahl",

  // Pass Chain
  "Pass Chain": "Fußballer verbinden",
  "Link the two players through shared clubs": "Verbinde die Spieler über gemeinsame Vereine",
  "Fewer links score higher": "Weniger Verbindungen bringen mehr Punkte",
  "Par —": "Par —",
  "Par {par} links": "Par {par} Verbindungen",
  "Who links to {name}?": "Wer passt zu {name}?",
  "Add": "Hinzufügen",
  "Already in the chain": "Schon in der Kette",
  "Unknown player — try another": "Unbekannter Spieler — versuch es mit einem anderen",
  "{a} never played with {b}": "{a} hat nie mit {b} gespielt",
  "Reset": "Zurücksetzen",
  "Linked!": "Verbunden!",
  "{n} links": "Verbindungen: {n}",
  "Par — perfect!": "Par — perfekt!",
  "par {n}": "Par {n}",
  "Next chain": "Nächste Kette",
  "START": "START",
  "TARGET": "ZIEL",

  // Penalty Shootout
  "Penalty Shootout": "Elfmeterschießen",
  "Round {n} / {total}": "Runde {n} / {total}",
  "You · AI": "Du · KI",
  "Answer to earn your shot": "Antworte und sichere dir deinen Schuss",
  "Opponent is stepping up…": "Der Gegner tritt an …",
  "AI is on target — get ready to dive!": "Die KI schießt aufs Tor — mach dich bereit zum Hechtsprung!",
  "AI answering…": "KI antwortet …",
  "Pick your corner — beat the keeper": "Wähle eine Ecke und überwinde den Torwart",
  "You're in goal — pick your dive!": "Du bist im Tor — wähle deine Sprungrichtung!",
  "GOAL!": "TOR!",
  "MISSED": "DANEBEN",
  "AI SCORES": "KI TRIFFT",
  "SAVED!": "GEHALTEN!",
  "Opponent's turn": "Der Gegner ist dran",
  "See result": "Ergebnis ansehen",
  "Next round": "Nächste Runde",
  "You win!": "Du gewinnst!",
  "Draw": "Unentschieden",
  "You lose": "Du verlierst",

  // Free Kicks (formerly Final Third)
  "Free Kicks": "Freistoß-Spiel",
  "Know football. Read the goal. Take the shot.": "Kenn den Fußball. Lies das Tor. Schieß.",
  "Balance": "Guthaben",
  "Pot": "Topf",
  "Your pot": "Dein Topf",
  "Stake {n} & attack": "Spiele um {n} weiter",
  "Min {n}": "Min. {n}",
  "Top up (demo)": "Aufladen (Demo)",
  "Answer to scout the keeper": "Antworte, um den Torwart auszukundschaften",
  "{n}s": "{n}s",
  "Time's up — blind shot": "Zeit um — Schuss ins Blaue",
  "Wrong — the correct answer stays. Blind shot.": "Falsch — die richtige Antwort bleibt. Schuss ins Blaue.",
  "Answer to scout the keeper — knowledge sharpens the shot, the risk stays.": "Antworte, um den Torwart auszukundschaften — Wissen verbessert den Schuss, das Risiko bleibt.",
  "Attack {n}": "Angriff {n}",
  "SCOUT REPORT ✓ — one save zone revealed": "SCOUT-BERICHT ✓ — eine Abwehrzone aufgedeckt",
  "No scout — blind shot pays more": "Kein Scout — der blinde Schuss bringt mehr",
  "Pick your shot zone": "Wähle deine Schusszone",
  "4 GOAL · 2 SAVE": "4 TOR · 2 GEHALTEN",
  "4 GOAL · 1 SAVE": "4 TOR · 1 GEHALTEN",
  "TAKE {amount}": "{amount} SICHERN",
  "NEXT ATTACK": "NÄCHSTER ANGRIFF",
  "Next attack risks the whole pot — the keeper resets.": "Beim nächsten Angriff steht der ganze Topf auf dem Spiel — der Torwart wird zurückgesetzt.",
  "The keeper read it — pot lost.": "Der Torwart hat ihn gelesen — der Topf ist weg.",
  "Cashed out {amount}!": "{amount} gesichert!",
  "Loading…": "Lädt …",
  "Could not load balance — tap to retry": "Guthaben konnte nicht geladen werden — tippe zum erneuten Laden",
  "New round": "Neue Runde",
  "Live wins": "Live-Siege",
  "Longest runs today": "Längste Serien heute",
  "{n} playing now": "Gerade spielen: {n}",
  "{pct}% answered correctly": "{pct}% richtig beantwortet",
  "{pct}% scored": "{pct}% haben getroffen",
  "{pct}% cashed out": "{pct}% haben gesichert",
  "{n} going NEXT ATTACK": "{n} wählen NÄCHSTER ANGRIFF",
  "You": "Du",
  "just now": "gerade eben",
  "{n}m ago": "vor {n} Min.",

  // Squad Collection
  "Squad Collection": "Mannschaftssammlung",
  "Answer to pull cards. Build your XI.": "Antworte und zieh Karten. Stell deine Startelf zusammen.",
  "Squad": "Mannschaft",
  "Answer to earn a pack": "Antworte und erhalte ein Kartenpaket",
  "Tap to open": "Zum Öffnen tippen",
  "Add to squad": "Zur Mannschaft hinzufügen",
  "Squad complete!": "Mannschaft vollständig!",
  "Reward: +2,000 🪙": "Belohnung: +2.000 🪙",
  "Start a new squad": "Neue Mannschaft starten",
  "Bronze": "Bronze",
  "Silver": "Silber",
  "Gold": "Gold",
  "Special": "Spezial",

  // Squad Spin
  "Easy": "Leicht",
  "Hard": "Schwer",
  "Expert": "Experte",
  "Name a player who fits every reel": "Nenne einen Spieler, der zu jeder Walze passt",
  "{n} reels": "Walzen: {n}",
  "Spin": "Drehen",
  "Spinning…": "Dreht sich …",
  "Name a player…": "Nenne einen Spieler …",
  "Go": "Los",
  "Hold a reel, respin the rest": "Halte eine Walze fest, dreh den Rest neu",
  "Respin −{n}": "Neu drehen −{n}",
  "Last 3 spins": "Letzte 3 Drehungen",
  "Missed": "Daneben",
  "Correct!": "Richtig!",
  "Only {pct}% named this": "Nur {pct}% haben ihn erraten",
  "rarity": "Seltenheit",
  "Time's up": "Zeit abgelaufen",
  "You could've said:": "Du hättest sagen können:",
  "No valid player for that combo!": "Kein passender Spieler für diese Kombination!",
  "Spin again": "Noch einmal drehen",
  "Hold reel": "Walze halten",
  "Release reel": "Walze freigeben",
  "Club": "Verein",
  "Position": "Position",
  "Nation": "Land",
  "Era": "Ära",
  "Trophy": "Titel",

  // Trivia Spin
  "easy": "leicht",
  "medium": "mittel",
  "hard": "schwer",
  "Answer to earn spins — the wheel pays out": "Antworte und erhalte Drehungen — das Rad vergibt Belohnungen",
  "Coins": "Münzen",
  "Question {n}": "Frage {n}",
  "+1 spin earned": "+1 Drehung erhalten",
  "No spin — try the next one": "Keine Drehung — versuch die nächste Frage",
  "{n} spins available": "Drehungen verfügbar: {n}",
  "JACKPOT!": "JACKPOT!",
  "Spin the wheel": "Rad drehen",
  "Answer to earn a spin": "Antworte und erhalte eine Drehung",

  // Data labels (fixtures / bet slip picks)
  "Full Time Result": "Endergebnis",
  "Next Goal": "Nächstes Tor",
  "Total Goals Over/Under 3.5": "Tore gesamt: über/unter 3,5",
  "Both Teams to Score — 2nd Half": "Beide Teams treffen — 2. Halbzeit",
  "No more goals": "Keine weiteren Tore",
  "Over 3.5": "Über 3,5",
  "Under 3.5": "Unter 3,5",
  "Yes": "Ja",
  "No": "Nein",
  "Champions League · Group Stage": "Champions League · Gruppenphase",
  "Real Madrid to win": "Sieg von Real Madrid",
  "Man City to win": "Sieg von Man City",
  "Bayern to win": "Sieg von Bayern München",
  "Goalkeeper": "Torwart",
  "Zone": "Zone",
  "Defender": "Verteidiger",
  "Midfielder": "Mittelfeldspieler",
  "Forward": "Stürmer",
  "World Cup": "Weltmeisterschaft",
  "League": "Liga",
  "Champions League": "Champions League",
  "League Title": "Meisterschaft",

  // Football Tic Tac Toe
  "Football Tic Tac Toe": "Fußball Tic Tac Toe",
  "Claim cells with players — three in a row wins": "Wähle Spieler — drei in einer Reihe gewinnen",
  "You · Opponent": "Du · Gegner",
  "Pick a cell and name a player who played for that club AND that nation. Your opponent answers back — line up three to win.": "Wähle ein Feld und nenne einen Spieler, der für diesen Verein UND dieses Land gespielt hat. Dein Gegner antwortet — bring drei in eine Reihe und gewinne.",
  "Start match": "Match starten",
  "Your turn — pick a cell": "Du bist dran — wähle ein Feld",
  "Not a match — turn passes": "Passt nicht — der Gegner ist dran",
  "Time's up — turn passes": "Zeit abgelaufen — der Gegner ist dran",
  "Pass": "Passen",
  "Opponent blanked — your turn!": "Dem Gegner fällt nichts ein — du bist dran!",
  "Opponent is thinking…": "Der Gegner überlegt …",
  "Opponent wins": "Der Gegner gewinnt",
  "Three in a row!": "Drei in einer Reihe!",
  "Board full — {a} cells vs {b}": "Feld voll — {a} Felder gegen {b}",
  "New grid": "Neues Raster",
  "{pct}%": "{pct}%",
  "Brazil": "Brasilien",
  "France": "Frankreich",
  "Argentina": "Argentinien",
  "Spain": "Spanien",
  "Netherlands": "Niederlande",
  "Portugal": "Portugal",

  // Survivor
  "Survivor": "Survivor",
  "One wrong answer and you are out — how long can you last?": "Eine falsche Antwort und du bist raus — wie lange hältst du durch?",
  "Streak": "Serie",
  "Sudden death": "Plötzlicher Tod",
  "Questions get harder as you go. {n} virtual players start with you — outlast them all.": "Die Fragen werden immer schwieriger. Du startest mit {n} virtuellen Spielern — halte länger durch als alle anderen.",
  "Best streak: {n}": "Beste Serie: {n}",
  "Enter the arena": "Arena betreten",
  "Still standing": "Noch dabei",
  "Eliminated": "Ausgeschieden",
  "Streak of {n}": "Serie: {n}",
  "You outlasted {pct}% of the field — {left} were still standing.": "Du hast {pct}% der Spieler überstanden — {left} waren noch dabei.",
  "New best!": "Neuer Rekord!",
  "Run it back": "Noch einmal spielen",
  "Last one standing!": "Du bist als Letzter übrig!",
  "You cleared every question in the bank — a perfect {n}-answer run.": "Du hast alle Fragen im Pool richtig beantwortet — eine perfekte Serie mit {n} Antworten.",

  // Hi-Lo Ride
  "Hi-Lo Ride": "Higher or Lower",
  "Call the higher stat — odds priced by how many get it right": "Wähle den höheren Wert — der Multiplikator hängt davon ab, wie viele richtig liegen",
  "Chain higher-or-lower calls. Every correct call multiplies your stake — hard matchups pay more. Cash out before you miss.": "Errate nacheinander, welcher Wert höher ist. Jede richtige Antwort vervielfacht deinen Betrag — bei schwierigen Duellen gibt es mehr. Sichere deine Münzen, bevor du falsch liegst.",
  "Stake {stake} & ride": "Spiele um {stake} weiter",
  "Step {n} · odds": "Schritt {n} · Multiplikator",
  "{pct}% of players call this one right": "{pct}% der Spieler liegen hier richtig",
  "Which is higher?": "Was ist höher?",
  "Higher": "Höher",
  "Pot is {pot}. Next matchup pays {odds}x → {next}.": "Der Topf beträgt {pot}. Das nächste Duell bringt {odds}x → {next}.",
  "Cash out {pot}": "{pot} sichern",
  "Ride on": "Weiterspielen",
  "Wrong call": "Falsch geraten",
  "The ride ends — your {stake} stake is gone.": "Die Serie endet — deine {stake} Münzen sind weg.",
  "New ride": "Neue Serie",
  "Cashed {amount}!": "{amount} gesichert!",
  "{n} correct calls — {mult}x your stake.": "{n} richtige Antworten — das {mult}-Fache deines Betrags.",

  // Trivia Mines
  "Trivia Mines": "Quiz-Minenfeld",
  "Dribble past hidden defenders — scout them with your knowledge": "Dribble an versteckten Verteidigern vorbei — finde sie mit deinem Wissen",
  "{d} defenders hide in {n} tiles. Every clean dribble grows the pot — hit a defender and lose it all. Answer questions to scout defenders out.": "{d} Verteidiger verstecken sich auf {n} Feldern. Jeder erfolgreiche Dribbling-Schritt vergrößert den Topf — triffst du einen Verteidiger, ist alles weg. Beantworte Fragen, um Verteidiger aufzuspüren.",
  "Stake {stake} & dribble": "Dribble um {stake} weiter",
  "Tackled": "Gestoppt",
  "Now · next tile": "Jetzt · nächstes Feld",
  "Scout ({n} left)": "Aufspüren ({n} übrig)",
  "{n} safe tiles opened · {d} defenders in play": "Sichere Felder: {n} · Verteidiger im Spiel: {d}",
  "Scout report — a defender is marked!": "Scout-Bericht — ein Verteidiger ist markiert!",
  "Scout failed — no intel": "Aufspüren fehlgeschlagen — keine Infos",
  "Answer to scout a defender": "Antworte und spüre einen Verteidiger auf",
  "Tackled!": "Gestoppt!",
  "A defender got you after {n} clean tiles — the pot is gone.": "Nach {n} sicheren Feldern hat dich ein Verteidiger erwischt — der Topf ist weg.",
  "New run": "Neuer Lauf",
  "{n} clean dribbles at {mult}x.": "{n} erfolgreiche Dribblings bei {mult}x.",

  // Quiz Board
  "Quiz Board": "Quiz-Tafel",
  "Pick tiles, bank the value — steal when the AI slips": "Wähle Felder und sichere die Punkte — bei einem Fehler der KI kannst du sie stehlen",
  "Nine tiles, three value tiers. Answer to bank the tile and keep control — miss and the AI can steal. Highest bank wins.": "Neun Felder, drei Punktestufen. Antworte richtig, sichere die Punkte und behalte den Zug — bei einem Fehler kann die KI sie stehlen. Wer die meisten Punkte hat, gewinnt.",
  "Start game": "Spiel starten",
  "Clubs": "Vereine",
  "Legends": "Legenden",
  "Tournaments": "Turniere",
  "Your board — pick a tile": "Deine Tafel — wähle ein Feld",
  "AI is picking a tile…": "Die KI wählt ein Feld …",
  "AI plays for {v}": "Die KI spielt um {v}",
  "AI banks it": "Die KI sichert die Punkte",
  "AI is wrong — steal it!": "Die KI liegt falsch — hol dir die Punkte!",
  "STEAL for {v}!": "HOL DIR {v}!",
  "For {v}": "Um {v}",
  "Final banks — you {a}, AI {b}.": "Endstand — du {a}, KI {b}.",
  "New board": "Neue Tafel",

  // Last One Standing
  "Last One Standing": "Wer bleibt übrig?",
  "{n} enter, one survives — make every cut": "{n} starten, einer bleibt — übersteh jede Runde",
  "Alive": "Noch dabei",
  "Battle royale quiz": "Battle-Royale-Quiz",
  "Every round the field is cut — answer right and fast to stay in it. Nine rounds from {n} players down to one.": "In jeder Runde scheiden Spieler aus — antworte richtig und schnell, um dabeizubleiben. In neun Runden geht es von {n} Spielern auf einen herunter.",
  "Kick off": "Anpfiff",
  "Continue": "Weiter",
  "A legendary goal replays on the coaching board. Name it — the first solve of each goal pays coins and XP.": "Ein legendäres Tor läuft auf der Taktiktafel erneut. Errate es — für jedes erstmals erratene Tor gibt es Münzen und XP.",
  "A legendary goal replays on the coaching board. Watch the moves and name the goal.": "Ein legendäres Tor läuft auf der Taktiktafel erneut. Beobachte die Spielzüge und errate das Tor.",
  "Back tomorrow": "Morgen wiederkommen",
  "All {limit} goals played — come back tomorrow": "Alle {limit} Tore gespielt — komm morgen wieder",
  "{left} of {limit} goals left today": "Heute noch {left} von {limit} Toren",
  "Daily coins earned — more tomorrow": "Tagesmünzen erhalten — morgen gibt es mehr",
  "Today: {today}/{cap} coins": "Heute: {today}/{cap} Münzen",
  "Your collection": "Deine Sammlung",
  "Fastest {cap} survive": "Die {cap} Schnellsten kommen weiter",
  "Field": "Feld",
  "You made the cut!": "Du bist weiter!",
  "#{rank} fastest — {cap} players remain.": "Platz {rank} — noch {cap} Spieler dabei.",
  "Cut!": "Ausgeschieden!",
  "Finished #{place} of {n}": "Platz {place} von {n}",
  "Round {r} was your last — the field marched on without you.": "Runde {r} war deine letzte — die anderen sind ohne dich weiter.",
  "Re-enter": "Wieder einsteigen",
  "CHAMPION!": "CHAMPION!",
  "{n} started. You are the last one standing.": "{n} sind gestartet. Du bist als Letzter übrig.",
  "Defend the title": "Verteidige den Titel",

  // Golden Goal
  "Golden Goal": "Golden Goal",
  "Sudden death — push the ball into their net": "Plötzlicher Tod — bring den Ball ins gegnerische Tor",
  "Round": "Runde",
  "Both of you answer the same question — faster and righter pushes the ball. Reach ±{n} and it is a goal. First goal wins.": "Ihr beantwortet dieselbe Frage — schneller und richtiger schiebt den Ball nach vorn. Bei ±{n} fällt ein Tor. Das erste Tor gewinnt.",
  "AI net": "KI-Tor",
  "Your attack": "Dein Angriff",
  "GOLDEN GOAL!": "GOLDEN GOAL!",
  "AI scores": "Die KI trifft",
  "Decided in {n} rounds.": "Entschieden in {n} Runden.",
  "Rematch": "Rückspiel",
  "vs": "gegen",

  // Career Race
  "Career Race": "Karrierestationen",
  "The transfer trail reveals — buzz before your rival": "Die Vereinsstationen werden enthüllt — antworte vor deinem Gegner",
  "Clubs appear one by one. Buzz early for 100 points — every extra club cuts the prize. Wrong buzz and you are out of the round. Best of {n}.": "Vereine erscheinen nacheinander. Antworte früh für 100 Punkte — jeder weitere Verein senkt die Belohnung. Falsche Antwort und du bist aus der Runde. Best-of-{n}.",
  "Start race": "Rennen starten",
  "Buzz now: {pts} pts": "Jetzt antworten: {pts} Punkte",
  "Locked out": "Gesperrt",
  "BUZZ!": "ANTWORT!",
  "Rival buzzed wrong — they are out!": "Dein Gegner lag falsch — er ist raus!",
  "Your rival is watching the same trail…": "Dein Gegner sieht dieselben Stationen …",
  "Whose career is this?": "Wessen Karriere ist das?",
  "Wrong — locked out!": "Falsch — du bist raus!",
  "⚡ Rival buzzes…": "⚡ Der Gegner antwortet …",
  "+{pts} — yours!": "+{pts} — deine!",
  "Rival takes {pts}": "Der Gegner erhält {pts}",
  "Nobody got it": "Niemand hat es erraten",
  "It was {name}": "Es war {name}",
  "You win the race!": "Du gewinnst das Rennen!",
  "Rival wins": "Der Gegner gewinnt",
  "Race again": "Noch ein Rennen",

  // Stat Sniper
  "Closest Wins": "Schätzfragen",
  "No options, no help — land your guess on the number": "Keine Auswahl, keine Hilfe — triff die Zahl",
  "Score": "Punktzahl",
  "{n} numeric stats — slide to your best guess. The closer you land, the more you score; a perfect hit pays a bullseye bonus.": "Schätze {n} Zahlen — schieb den Regler auf deine beste Schätzung. Je näher du dran bist, desto mehr Punkte gibt es; ein Volltreffer bringt Bonuspunkte.",
  "Take aim": "Zielen",
  "Target {n} / {total}": "Ziel {n} / {total}",
  "Lock it in": "Festlegen",
  "Answer": "Antwort",
  "BULLSEYE! +{pts}": "VOLLTREFFER! +{pts}",
  "+{pts} pts": "+{pts} Punkte",
  "You were {diff} off.": "Du lagst um {diff} daneben.",
  "Next target": "Nächstes Ziel",
  "Elite sniper!": "Volltreffer-Profi!",
  "Sharp shooter": "Treffsicher",
  "On the range": "Auf dem Schießstand",
  "Blindfolded": "Mit verbundenen Augen",
  "{score} points over {n} targets": "{score} Punkte bei {n} Zielen",
  "Reload": "Neu laden",

  // Free Kicks — boost rules (50/50 shot, answers raise the payout)
  "Every shot is a 50/50 duel with the keeper. Answer questions to boost the payout — a wrong answer resets the boost.": "Jeder Schuss ist ein 50:50-Duell mit dem Torwart. Beantworte Fragen und erhöhe die Belohnung — bei einer falschen Antwort verfällt der Bonus.",
  "Boost {a}/{max} · ×{mult}": "Bonus {a}/{max} · ×{mult}",
  "BOOSTED! Payout is now ×{mult}": "BONUS AKTIV! Belohnung jetzt ×{mult}",
  "Wrong — boost resets to ×{mult}": "Falsch — Bonus zurück auf ×{mult}",
  "50/50 shot · current payout ×{mult}": "50:50-Schuss · aktuelle Belohnung ×{mult}",
  "Answer · boost to ×{next}": "Antworte · Bonus auf ×{next}",
  "Boosts raise the payout — the shot stays 50/50.": "Boni erhöhen die Belohnung — die Chance bleibt 50:50.",
  "Boosting…": "Bonus wird erhöht …",
  "Time's up — boost resets to ×{mult}": "Zeit abgelaufen — Bonus zurück auf ×{mult}",
  "Left or right — the keeper picks one": "Links oder rechts — der Torwart wählt eine Seite",
  "Pick your corner": "Wähle eine Ecke",
  "Next attack risks the whole pot — the boost resets.": "Beim nächsten Angriff steht der ganze Topf auf dem Spiel — der Bonus wird zurückgesetzt.",

  // Free Kicks — run-multiplier HUD
  "Run ×{run} → ×{next}": "Serie ×{run} → ×{next}",
  "Run": "Serie",
  "Answering is locked this attack — take the shot.": "In diesem Angriff kannst du nicht antworten — schieß.",
  "Sound settings": "Toneinstellungen",
  "Crowd": "Publikum",
  "Effects": "Effekte",
  "Off": "Aus",
  "More open zones = better odds of keeping the run alive.": "Mehr offene Zonen = bessere Chancen, die Serie fortzusetzen.",

  // Free Kicks — Option C (open-zones) rules
  "The goal opens with 2 zones and one hidden keeper. Answer questions to open up to 6 — a wrong answer slams it back to 2.": "Das Tor beginnt mit 2 Zonen und einem versteckten Torwart. Beantworte Fragen und öffne bis zu 6 — bei einer falschen Antwort geht es zurück auf 2.",
  "{k} open · {pct}% · ×{mult}": "{k} offen · {pct}% · ×{mult}",
  "Zone opened! {k} of {max} in play": "Zone geöffnet! {k} von {max} im Spiel",
  "Pick your angle and shoot · {pot} → {next}": "Wähle den Winkel und schieß · {pot} → {next}",
  "Wrong — goal slams back to {n} zones": "Falsch — zurück auf {n} Torzonen",
  "{k} zones open · 1 keeper hidden · {pct}% goal": "Zonen offen: {k} · 1 Torwart versteckt · {pct}% Torchance",
  "Answer · open zone {n}": "Antworte · öffne Zone {n}",
  "Shoot · {pot} → {next}": "Schieß · {pot} → {next}",
  "More zones = safer shot, smaller payout — the maths favours knowing.": "Mehr Zonen = sicherer Schuss, kleinere Belohnung — Wissen zahlt sich aus.",
  "Opening the goal…": "Tor wird geöffnet …",
  "Read the question…": "Lies die Frage …",
  "Time's up — the goal slams back to {n} zones": "Zeit abgelaufen — zurück auf {n} Torzonen",
  "Wrong — the goal slams back to {n} zones": "Falsch — zurück auf {n} Torzonen",
  "{k} zones open — one hides the keeper": "{k} Zonen offen — hinter einer ist der Torwart",
  "{pct}% goal": "{pct}% Torchance",
  "Next attack risks the whole pot — the goal resets to {n} zones.": "Beim nächsten Angriff steht der ganze Topf auf dem Spiel — das Tor geht zurück auf {n} Zonen.",

  // FIFA Cards
  "Guess the Card": "Spielerkarte erraten",
  "FIFA Cards": "Goldkarte erraten",
  "Spin for a FIFA edition, then name the player from the stats": "Dreh eine FIFA-Ausgabe und errate den Spieler anhand der Werte",
  "Spinning the edition…": "FIFA-Ausgabe wird ausgewählt …",
  "Locked in!": "Festgelegt!",
  "Guess the player from the drawn edition": "Errate den Spieler anhand der gezogenen Ausgabe",
  "Card": "Karte",
  "No clues": "Keine Hinweise",
  "{clue} unlocks in": "Hinweis {clue} wird freigeschaltet in",
  "Final clue — answer now": "Letzter Hinweis — jetzt antworten",
  "Name the player…": "Nenne den Spieler …",
  "Named on stats alone": "Nur anhand der Werte erraten",
  "Guessed with {n} clue(s)": "Mit {n} Hinweisen erraten",
  "Round complete": "Runde abgeschlossen",
  "of {max} · {n}/{total} named": "von {max} · {n}/{total} erraten",

  // Guess the Card — daily challenge
  "Card {n}/{total}": "Karte {n}/{total}",
  "Spin for a FIFA edition, then name the player from the card": "Dreh eine FIFA-Ausgabe und errate den Spieler anhand der Karte",
  "Give up": "Aufgeben",
  "Wrong!": "Falsch!",
  // Difficulty labels
  "Medium": "Mittel",
  "Very hard": "Sehr schwer",
  "Medium/Easy": "Mittel/Leicht",
  "Pre-FIFA 20": "Vor FIFA 20",
  "Tap a lock to reveal a clue": "Tippe auf das Schloss, um einen Hinweis aufzudecken",
  "Clue revealed": "Hinweis aufgedeckt",
  "The answer": "Die Antwort",
  "{clue} unlocks next": "Als Nächstes wird {clue} freigeschaltet",
  "Nice — +{p}": "Gut gemacht — +{p}",
  "Daily complete!": "Tages-Challenge abgeschlossen!",
  "Today's round is done": "Die heutige Runde ist vorbei",
  "Coins earned": "Münzen erhalten",
  "Solved": "Erraten",
  "New cards at reset": "Neue Karten nach der Aktualisierung",
  "Come back tomorrow": "Komm morgen wieder",
  "Reveal clue": "Hinweis aufdecken",
  "Hidden clue": "Versteckter Hinweis",
  "Back to challenges": "Zurück zu den Challenges",
  "A gold card, stats only — name the player across 10 editions.": "Eine Goldkarte, nur Werte — errate den Spieler in 10 Ausgaben.",

  // Card Detective (daily + demo)
  "Tap a lock to buy that clue": "Tippe auf das Schloss, um den Hinweis freizuschalten",
  "Today {n} pts": "Heute: {n} Punkte",
  "Correct now = {n} pts": "Jetzt richtig = {n} Punkte",
  "Not {name} · −{n} coins": "Nicht {name} · −{n} Münzen",
  "Revealed": "Aufgedeckt",
  "Reveal {clue} for {n} coins": "Decke {clue} für {n} Münzen auf",
  "Hint budget": "Hinweisbudget",
  "Hint budget: {n} pts": "Hinweisbudget: {n} Punkte",
  "Hint budget resets every card · separate from your coins": "Das Budget startet bei jeder Karte neu · nicht deine Münzen",
  "Not {name} · −{n} pts": "Nicht {name} · −{n} Punkte",
  "Reveal {clue} for {n} pts": "Decke {clue} für {n} Punkte auf",
  "Buy clues on the card, name the player, keep your points": "Schalte Hinweise auf der Karte frei, nenne den Spieler und behalte deine Punkte",
  "Hints bought: {n}": "Gekaufte Hinweise: {n}",
  "No hints bought": "Keine Hinweise gekauft",
  "+{n} pts saved by skipping hints": "+{n} Punkte gespart ohne Hinweise",
  "No points on a surrendered card": "Aufgegebene Karten bringen keine Punkte",
  "Next card": "Nächste Karte",
  "See results": "Ergebnisse ansehen",
  "{n} pts": "{n} Punkte",
  "Points saved through deduction": "Durch Kombinieren gesparte Punkte",
  "Across {n} solved cards": "Bei {n} gelösten Karten",
  "photo": "Foto",
  "nation": "Land",
  "position": "Position",
  "club": "Verein",
  "league": "Liga",
  "POS": "POS",
  "NATION": "LAND",
  "LEAGUE": "LIGA",
  "CLUB": "VEREIN",
  "Dealing…": "Karten werden ausgeteilt …",
  "Your card": "Deine Karte",
  "Buy clues on the card, name the player, keep your coins": "Schalte Hinweise auf der Karte frei, nenne den Spieler und behalte deine Münzen",
  "Start": "Starten",
  "Run complete": "Lauf abgeschlossen",
  "Rival": "Rivale",
};

const DICTS: Partial<Record<MiniLocale, Record<string, string>>> = { ka: KA, es: ES, tr: TR, ru: RU, pt: PT, de: DE };

export function useMiniLocale(): MiniLocale {
  const { locale } = useLocale();
  return locale;
}

/** t('Question {n} / {total}', { n: 1, total: 5 }) — EN key, {slot} substitution. */
export function useMiniT() {
  const locale = useMiniLocale();
  return useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let out = DICTS[locale]?.[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          out = out.split(`{${k}}`).join(String(v));
        }
      }
      return out;
    },
    [locale],
  );
}
